import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import './App.css';
import {
  playStrumMetronomeTick,
  STRUM_METRONOME_OPTIONS,
  type StrumMetronomeStyle,
} from './strumMetronomeAudio';
import {
  playGuitarStringFret,
  pickStringFretForMidi,
  playNylonStrumClick,
  resumeAudioContext,
} from './guitarAudio';

const earIconStroke = {
  width: 20,
  height: 20,
  viewBox: '0 0 24 24',
  fill: 'none' as const,
  stroke: 'currentColor',
  strokeWidth: 2,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
  'aria-hidden': true as const,
};

function IconVolume({ className }: { className?: string }) {
  return (
    <svg {...earIconStroke} className={className}>
      <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
      <path d="M15.54 8.46a5 5 0 0 1 0 7.07M19.07 4.93a9 9 0 0 1 0 14.14" />
    </svg>
  );
}

function IconRefresh({ className }: { className?: string }) {
  return (
    <svg {...earIconStroke} className={className}>
      <path d="M23 4v6h-6M1 20v-6h6" />
      <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
    </svg>
  );
}

function IconZap({ className }: { className?: string }) {
  return (
    <svg {...earIconStroke} className={className}>
      <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
    </svg>
  );
}

function IconFilm({ className }: { className?: string }) {
  return (
    <svg {...earIconStroke} className={className}>
      <rect x="2" y="2" width="20" height="20" rx="2.18" ry="2.18" />
      <line x1="7" y1="2" x2="7" y2="22" />
      <line x1="17" y1="2" x2="17" y2="22" />
      <line x1="2" y1="12" x2="22" y2="12" />
      <line x1="2" y1="7" x2="7" y2="7" />
      <line x1="2" y1="17" x2="7" y2="17" />
      <line x1="17" y1="17" x2="22" y2="17" />
      <line x1="17" y1="7" x2="22" y2="7" />
    </svg>
  );
}

function IconChevronDown({ className }: { className?: string }) {
  return (
    <svg {...earIconStroke} className={className}>
      <polyline points="6 9 12 15 18 9" />
    </svg>
  );
}

function IconChevronRight({ className }: { className?: string }) {
  return (
    <svg {...earIconStroke} className={className}>
      <polyline points="9 18 15 12 9 6" />
    </svg>
  );
}

function IconArrowUp({ className }: { className?: string }) {
  return (
    <svg {...earIconStroke} className={className}>
      <line x1="12" y1="19" x2="12" y2="5" />
      <polyline points="5 12 12 5 19 12" />
    </svg>
  );
}

function IconArrowDown({ className }: { className?: string }) {
  return (
    <svg {...earIconStroke} className={className}>
      <line x1="12" y1="5" x2="12" y2="19" />
      <polyline points="19 12 12 19 5 12" />
    </svg>
  );
}

function IconX({ className }: { className?: string }) {
  return (
    <svg {...earIconStroke} className={className}>
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  );
}

type ScaleType = 
  | 'Major' 
  | 'Natural Minor' 
  | 'Major Pentatonic' 
  | 'Minor Pentatonic'
  | 'Blues'
  | 'Dorian'
  | 'Phrygian'
  | 'Lydian'
  | 'Mixolydian'
  | 'Locrian'
  | 'Harmonic Minor'
  | 'Melodic Minor'
  | 'Diminished'
  | 'Whole Tone'
  | 'Major Blues';

/** Common = everyday practice scales; Advanced = modes & extended scales */
type ScaleCategory = 'Common' | 'Advanced';

const SCALE_GROUPS: Record<ScaleCategory, ScaleType[]> = {
  Common: [
    'Minor Pentatonic',
    'Major Pentatonic',
    'Blues',
    'Major Blues',
    'Major',
    'Natural Minor',
  ],
  Advanced: [
    'Dorian',
    'Phrygian',
    'Lydian',
    'Mixolydian',
    'Locrian',
    'Harmonic Minor',
    'Melodic Minor',
    'Diminished',
    'Whole Tone',
  ],
};

type Mode = 'scale' | 'chord' | 'strum' | 'ear' | 'notes';
type ChordDisplay = 'notes' | 'fingers';
type ChordCategory = 'Standard' | 'Advanced' | 'CAGED System' | 'Jazz' | 'Barre Chords' | 'Power Chords';
type ChordPositionMode = 'single' | 'all-positions';

const SCALES: Record<ScaleType, number[]> = {
  'Major': [0, 2, 4, 5, 7, 9, 11],
  'Natural Minor': [0, 2, 3, 5, 7, 8, 10],
  'Major Pentatonic': [0, 2, 4, 7, 9],
  'Minor Pentatonic': [0, 3, 5, 7, 10],
  'Blues': [0, 3, 5, 6, 7, 10],
  'Dorian': [0, 2, 3, 5, 7, 9, 10],
  'Phrygian': [0, 1, 3, 5, 7, 8, 10],
  'Lydian': [0, 2, 4, 6, 7, 9, 11],
  'Mixolydian': [0, 2, 4, 5, 7, 9, 10],
  'Locrian': [0, 1, 3, 5, 6, 8, 10],
  'Harmonic Minor': [0, 2, 3, 5, 7, 8, 11],
  'Melodic Minor': [0, 2, 3, 5, 7, 9, 11],
  'Diminished': [0, 2, 3, 5, 6, 8, 9, 11],
  'Whole Tone': [0, 2, 4, 6, 8, 10],
  'Major Blues': [0, 2, 3, 4, 7, 9],
};

// Chord definitions: [string][fret, finger] (-1 means don't play, 0 means open)
type ChordShape = {
  name: string;
  positions: Array<[number, number]>; // [fret, finger] for each string (high E to low E)
};

const CHORD_CATEGORIES: Record<ChordCategory, Record<string, ChordShape>> = {
  'Standard': {
    'C': { name: 'C', positions: [[0, 0], [1, 1], [0, 0], [2, 2], [3, 3], [-1, 0]] },
    'G': { name: 'G', positions: [[3, 3], [0, 0], [0, 0], [0, 0], [2, 1], [3, 2]] },
    'D': { name: 'D', positions: [[2, 2], [3, 3], [2, 1], [0, 0], [-1, 0], [-1, 0]] },
    'A': { name: 'A', positions: [[0, 0], [2, 2], [2, 3], [2, 1], [0, 0], [-1, 0]] },
    'E': { name: 'E', positions: [[0, 0], [0, 0], [1, 1], [2, 3], [2, 2], [0, 0]] },
    'Am': { name: 'Am', positions: [[0, 0], [1, 1], [2, 3], [2, 2], [0, 0], [-1, 0]] },
    'Em': { name: 'Em', positions: [[0, 0], [0, 0], [0, 0], [2, 2], [2, 1], [0, 0]] },
    'Dm': { name: 'Dm', positions: [[1, 1], [3, 3], [2, 2], [0, 0], [-1, 0], [-1, 0]] },
  },
  'Advanced': {
    'F': { name: 'F', positions: [[1, 1], [1, 1], [2, 2], [3, 4], [3, 3], [1, 1]] },
    'B': { name: 'B', positions: [[2, 1], [4, 4], [4, 3], [4, 2], [2, 1], [-1, 0]] },
    'Bm': { name: 'Bm', positions: [[2, 1], [3, 3], [4, 4], [4, 2], [2, 1], [-1, 0]] },
    'C7': { name: 'C7', positions: [[0, 0], [1, 1], [3, 4], [2, 2], [3, 3], [-1, 0]] },
    'G7': { name: 'G7', positions: [[1, 1], [0, 0], [0, 0], [0, 0], [2, 2], [3, 3]] },
    'D7': { name: 'D7', positions: [[2, 2], [1, 1], [2, 3], [0, 0], [-1, 0], [-1, 0]] },
    'A7': { name: 'A7', positions: [[0, 0], [2, 2], [0, 0], [2, 1], [0, 0], [-1, 0]] },
    'E7': { name: 'E7', positions: [[0, 0], [0, 0], [1, 1], [0, 0], [2, 2], [0, 0]] },
    'Fmaj7': { name: 'Fmaj7', positions: [[0, 0], [1, 1], [2, 2], [3, 3], [3, 3], [1, 1]] },
    'Cadd9': { name: 'Cadd9', positions: [[0, 0], [3, 3], [0, 0], [2, 1], [3, 2], [-1, 0]] },
    'Dsus2': { name: 'Dsus2', positions: [[0, 0], [3, 3], [2, 1], [0, 0], [-1, 0], [-1, 0]] },
    'Dsus4': { name: 'Dsus4', positions: [[3, 4], [3, 3], [2, 1], [0, 0], [-1, 0], [-1, 0]] },
    'Asus2': { name: 'Asus2', positions: [[0, 0], [2, 2], [2, 1], [0, 0], [0, 0], [-1, 0]] },
    'Asus4': { name: 'Asus4', positions: [[0, 0], [3, 3], [2, 2], [2, 1], [0, 0], [-1, 0]] },
  },
  'CAGED System': {
    'C (C Shape)': { name: 'C (C Shape)', positions: [[0, 0], [1, 1], [0, 0], [2, 2], [3, 3], [-1, 0]] },
    'A (A Shape)': { name: 'A (A Shape)', positions: [[0, 0], [2, 2], [2, 3], [2, 1], [0, 0], [-1, 0]] },
    'G (G Shape)': { name: 'G (G Shape)', positions: [[3, 3], [0, 0], [0, 0], [0, 0], [2, 1], [3, 2]] },
    'E (E Shape)': { name: 'E (E Shape)', positions: [[0, 0], [0, 0], [1, 1], [2, 3], [2, 2], [0, 0]] },
    'D (D Shape)': { name: 'D (D Shape)', positions: [[2, 2], [3, 3], [2, 1], [0, 0], [-1, 0], [-1, 0]] },
    'C (A Shape at 3rd)': { name: 'C (A Shape at 3rd)', positions: [[3, 1], [5, 3], [5, 4], [5, 2], [3, 1], [-1, 0]] },
    'G (E Shape at 3rd)': { name: 'G (E Shape at 3rd)', positions: [[3, 1], [3, 1], [4, 2], [5, 4], [5, 3], [3, 1]] },
    'D (C Shape at 2nd)': { name: 'D (C Shape at 2nd)', positions: [[2, 1], [3, 2], [2, 1], [4, 3], [5, 4], [-1, 0]] },
    'A (G Shape at 2nd)': { name: 'A (G Shape at 2nd)', positions: [[5, 4], [2, 1], [2, 1], [2, 1], [4, 3], [5, 4]] },
    'E (D Shape at 2nd)': { name: 'E (D Shape at 2nd)', positions: [[4, 3], [5, 4], [4, 2], [2, 1], [-1, 0], [-1, 0]] },
  },
  'Jazz': {
    'Cmaj7': { name: 'Cmaj7', positions: [[0, 0], [0, 0], [0, 0], [2, 2], [3, 3], [-1, 0]] },
    'Cm7': { name: 'Cm7', positions: [[3, 2], [4, 3], [3, 1], [5, 4], [3, 1], [-1, 0]] },
    'C7': { name: 'C7', positions: [[0, 0], [1, 1], [3, 4], [2, 2], [3, 3], [-1, 0]] },
    'Cmaj9': { name: 'Cmaj9', positions: [[0, 0], [3, 3], [0, 0], [2, 1], [3, 2], [-1, 0]] },
    'Cm9': { name: 'Cm9', positions: [[3, 2], [3, 1], [3, 1], [3, 1], [3, 1], [-1, 0]] },
    'C6': { name: 'C6', positions: [[0, 0], [1, 1], [2, 2], [2, 3], [3, 4], [-1, 0]] },
    'Cm6': { name: 'Cm6', positions: [[1, 1], [1, 1], [3, 3], [1, 1], [3, 4], [-1, 0]] },
    'Cdim7': { name: 'Cdim7', positions: [[2, 2], [3, 3], [1, 1], [2, 2], [-1, 0], [-1, 0]] },
    'C7b5': { name: 'C7b5', positions: [[4, 4], [3, 3], [2, 2], [3, 3], [-1, 0], [-1, 0]] },
    'Cm7b5': { name: 'Cm7b5', positions: [[3, 2], [4, 3], [3, 1], [4, 4], [-1, 0], [-1, 0]] },
    'C7#9': { name: 'C7#9', positions: [[1, 1], [3, 3], [1, 1], [2, 2], [-1, 0], [-1, 0]] },
    'C13': { name: 'C13', positions: [[0, 0], [1, 1], [1, 1], [2, 2], [3, 3], [3, 3]] },
  },
  'Barre Chords': {
    'F (E Shape)': { name: 'F (E Shape)', positions: [[1, 1], [1, 1], [2, 2], [3, 4], [3, 3], [1, 1]] },
    'Fm (Em Shape)': { name: 'Fm (Em Shape)', positions: [[1, 1], [1, 1], [1, 1], [3, 3], [3, 2], [1, 1]] },
    'Bb (A Shape)': { name: 'Bb (A Shape)', positions: [[1, 1], [3, 3], [3, 4], [3, 2], [1, 1], [-1, 0]] },
    'Bbm (Am Shape)': { name: 'Bbm (Am Shape)', positions: [[1, 1], [2, 2], [3, 4], [3, 3], [1, 1], [-1, 0]] },
    'C# (C Shape)': { name: 'C# (C Shape)', positions: [[1, 1], [2, 2], [1, 1], [3, 3], [4, 4], [-1, 0]] },
    'Eb (D Shape)': { name: 'Eb (D Shape)', positions: [[3, 2], [4, 3], [3, 1], [1, 1], [-1, 0], [-1, 0]] },
    'G# (G Shape)': { name: 'G# (G Shape)', positions: [[4, 1], [1, 1], [1, 1], [1, 1], [3, 3], [4, 4]] },
    'F#m (Em Shape at 2nd)': { name: 'F#m (Em Shape at 2nd)', positions: [[2, 1], [2, 1], [2, 1], [4, 3], [4, 2], [2, 1]] },
    'B (A Shape at 2nd)': { name: 'B (A Shape at 2nd)', positions: [[2, 1], [4, 3], [4, 4], [4, 2], [2, 1], [-1, 0]] },
    'C (G Shape at 3rd)': { name: 'C (G Shape at 3rd)', positions: [[5, 4], [3, 1], [3, 1], [3, 1], [5, 3], [-1, 0]] },
  },
  'Power Chords': {
    'C5': { name: 'C5', positions: [[-1, 0], [-1, 0], [5, 2], [5, 3], [3, 1], [-1, 0]] },
    'D5': { name: 'D5', positions: [[-1, 0], [-1, 0], [7, 2], [7, 3], [5, 1], [-1, 0]] },
    'E5': { name: 'E5', positions: [[-1, 0], [-1, 0], [-1, 0], [2, 2], [2, 1], [0, 0]] },
    'F5': { name: 'F5', positions: [[-1, 0], [-1, 0], [-1, 0], [3, 3], [3, 2], [1, 1]] },
    'G5': { name: 'G5', positions: [[-1, 0], [-1, 0], [-1, 0], [5, 3], [5, 2], [3, 1]] },
    'A5': { name: 'A5', positions: [[-1, 0], [-1, 0], [-1, 0], [-1, 0], [2, 2], [0, 0]] },
    'B5': { name: 'B5', positions: [[-1, 0], [-1, 0], [-1, 0], [-1, 0], [4, 2], [2, 1]] },
    'C#5': { name: 'C#5', positions: [[-1, 0], [-1, 0], [6, 2], [6, 3], [4, 1], [-1, 0]] },
    'D#5': { name: 'D#5', positions: [[-1, 0], [-1, 0], [8, 2], [8, 3], [6, 1], [-1, 0]] },
    'F#5': { name: 'F#5', positions: [[-1, 0], [-1, 0], [-1, 0], [4, 3], [4, 2], [2, 1]] },
    'G#5': { name: 'G#5', positions: [[-1, 0], [-1, 0], [-1, 0], [6, 3], [6, 2], [4, 1]] },
    'A#5': { name: 'A#5', positions: [[-1, 0], [-1, 0], [-1, 0], [-1, 0], [3, 2], [1, 1]] },
  }
};

const NOTES = ['C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B'];
const STRING_TUNINGS = [4, 11, 7, 2, 9, 4]; // E B G D A E (from high E to low E)
const NUM_FRETS = 14;
const FRET_MARKERS = [3, 5, 7, 9, 12]; // Single dot positions
const DOUBLE_FRET_MARKER = 12; // Double dot position

// Interval training data
type IntervalData = {
  name: string;
  semitones: number;
  description: string;
  ascending: {
    song: string;
    artist: string;
    spotifyUri?: string;
    youtubeId?: string;
    startTime?: number; // Time in seconds where interval occurs
  };
  descending: {
    song: string;
    artist: string;
    spotifyUri?: string;
    youtubeId?: string;
    startTime?: number;
  };
};

const INTERVALS: IntervalData[] = [
  {
    name: 'Minor 2nd',
    semitones: 1,
    description: 'Half step - Creates tension and dissonance',
    ascending: {
      song: 'Jaws Theme',
      artist: 'John Williams',
      youtubeId: 'A9QTSyLwd4w',
      startTime: 6,
    },
    descending: {
      song: 'Für Elise',
      artist: 'Beethoven',
      youtubeId: 'wfF0zHeU3Zs',
      startTime: 0,
    },
  },
  {
    name: 'Major 2nd',
    semitones: 2,
    description: 'Whole step - Smooth and pleasant',
    ascending: {
      song: 'Happy Birthday',
      artist: 'Traditional',
      youtubeId: 'inS9gAgSENE',
      startTime: 0,
    },
    descending: {
      song: 'Mary Had a Little Lamb',
      artist: 'Traditional',
      youtubeId: 'N8nGig78lNs',
      startTime: 0,
    },
  },
  {
    name: 'Minor 3rd',
    semitones: 3,
    description: 'Sad, melancholic sound',
    ascending: {
      song: 'Greensleeves',
      artist: 'Traditional',
      youtubeId: 'fzk13OqfFUA',
      startTime: 0,
    },
    descending: {
      song: 'Hey Jude',
      artist: 'The Beatles',
      spotifyUri: 'spotify:track:0aym2LBJBk9DAYuHHutrIl',
      youtubeId: 'A_MjCqQoLLA',
      startTime: 0,
    },
  },
  {
    name: 'Major 3rd',
    semitones: 4,
    description: 'Bright and happy',
    ascending: {
      song: 'When the Saints Go Marching In',
      artist: 'Traditional',
      youtubeId: 'nxKHu79lfsU',
      startTime: 0,
    },
    descending: {
      song: 'Swing Low, Sweet Chariot',
      artist: 'Traditional',
      youtubeId: 'z0fKbYRNt88',
      startTime: 0,
    },
  },
  {
    name: 'Perfect 4th',
    semitones: 5,
    description: 'Open, stable sound',
    ascending: {
      song: 'Here Comes the Bride',
      artist: 'Wagner',
      youtubeId: 'KddhSYezYQU',
      startTime: 0,
    },
    descending: {
      song: 'Oh Come All Ye Faithful',
      artist: 'Traditional',
      youtubeId: '1tM5pwvUGMI',
      startTime: 0,
    },
  },
  {
    name: 'Tritone',
    semitones: 6,
    description: 'Augmented 4th/Diminished 5th - Very dissonant',
    ascending: {
      song: 'The Simpsons Theme',
      artist: 'Danny Elfman',
      youtubeId: 'Fttst9dG6Ko',
      startTime: 0,
    },
    descending: {
      song: 'Black Sabbath',
      artist: 'Black Sabbath',
      spotifyUri: 'spotify:track:49Ii24oJd62FoHmmVmCT0k',
      youtubeId: '0lVdMbUx1_k',
      startTime: 0,
    },
  },
  {
    name: 'Perfect 5th',
    semitones: 7,
    description: 'Strong, powerful sound',
    ascending: {
      song: 'Star Wars Theme',
      artist: 'John Williams',
      youtubeId: '_D0ZQPqeJkk',
      startTime: 0,
    },
    descending: {
      song: 'Flintstones Theme',
      artist: 'Hoyt Curtin',
      youtubeId: 'qpGx4foRdPw',
      startTime: 0,
    },
  },
  {
    name: 'Minor 6th',
    semitones: 8,
    description: 'Sad, yearning quality',
    ascending: {
      song: 'The Entertainer',
      artist: 'Scott Joplin',
      youtubeId: '7_ZotkSW8Lk',
      startTime: 0,
    },
    descending: {
      song: 'Love Story',
      artist: 'Taylor Swift',
      spotifyUri: 'spotify:track:1vrd6UOGamcKNGnSHJQlSt',
      youtubeId: '8xg3vE8Ie_E',
      startTime: 73,
    },
  },
  {
    name: 'Major 6th',
    semitones: 9,
    description: 'Bright, hopeful sound',
    ascending: {
      song: 'My Bonnie Lies Over the Ocean',
      artist: 'Traditional',
      youtubeId: 'LNfP-De1BoY',
      startTime: 0,
    },
    descending: {
      song: 'Nobody Knows the Trouble I\'ve Seen',
      artist: 'Traditional',
      youtubeId: 'SVKKRzemX_w',
      startTime: 0,
    },
  },
  {
    name: 'Minor 7th',
    semitones: 10,
    description: 'Blues sound, slightly dissonant',
    ascending: {
      song: 'Star Trek Theme',
      artist: 'Alexander Courage',
      youtubeId: 'hdjL8WXjlGI',
      startTime: 2,
    },
    descending: {
      song: 'Watermelon Man',
      artist: 'Herbie Hancock',
      spotifyUri: 'spotify:track:5w0oKJZfZhVCV9xJae8pzF',
      youtubeId: '4bjPlBC4h_8',
      startTime: 5,
    },
  },
  {
    name: 'Major 7th',
    semitones: 11,
    description: 'Dreamy, jazzy sound',
    ascending: {
      song: 'Take On Me',
      artist: 'a-ha',
      spotifyUri: 'spotify:track:2WfaOiMkCvy7F5fcp2zZ8L',
      youtubeId: 'djV11Xbc914',
      startTime: 22,
    },
    descending: {
      song: 'I Love You',
      artist: 'Cole Porter',
      youtubeId: 'dQw4w9WgXcQ',
      startTime: 0,
    },
  },
  {
    name: 'Octave',
    semitones: 12,
    description: 'Same note, different register',
    ascending: {
      song: 'Somewhere Over the Rainbow',
      artist: 'Harold Arlen',
      youtubeId: 'PSZxmZmBfnU',
      startTime: 52,
    },
    descending: {
      song: 'Willow Weep for Me',
      artist: 'Ann Ronell',
      youtubeId: 'uU2GKb23KRY',
      startTime: 0,
    },
  },
];

function getScaleNotes(root: string, scale: number[]) {
  const rootIndex = NOTES.indexOf(root);
  return scale.map(interval => NOTES[(rootIndex + interval) % 12]);
}

// Chord intervals by quality (semitones from root). Used for all-positions and chord-tone colors.
const CHORD_INTERVALS_BY_QUALITY: Record<string, number[]> = {
  '': [0, 4, 7],       // major
  'm': [0, 3, 7],      // minor
  '7': [0, 4, 7, 10],  // dominant 7
  'maj7': [0, 4, 7, 11],
  'm7': [0, 3, 7, 10],
  'dim': [0, 3, 6],
  'dim7': [0, 3, 6, 9],
};

function getChordIntervals(chordName: string): number[] | null {
  const base = chordName.replace(/\s*\(.*\)$/, '').trim();
  const match = base.match(/^([A-G#]+)(maj7|m7|dim7|dim|7|m)?$/i);
  if (!match) return null;
  const quality = (match[2] || '').toLowerCase();
  return CHORD_INTERVALS_BY_QUALITY[quality] ?? CHORD_INTERVALS_BY_QUALITY[''] ?? null;
}

function getChordToneType(noteIndex: number, chordName: string): 'root' | '3rd' | '5th' | '7th' | null {
  const intervals = getChordIntervals(chordName);
  if (!intervals) return null;
  const rootNote = chordName.replace(/\s*\(.*\)$/, '').trim().match(/^([A-G#]+)/)?.[1];
  if (!rootNote) return null;
  const rootIndex = NOTES.indexOf(rootNote);
  if (rootIndex === -1) return null;
  const semitones = (noteIndex - rootIndex + 12) % 12;
  if (semitones === 0) return 'root';
  if (semitones === 3 || semitones === 4) return '3rd';
  if (semitones === 6 || semitones === 7) return '5th';
  if (semitones === 9 || semitones === 10 || semitones === 11) return '7th';
  return null;
}

// Function to find all positions of a chord on the fretboard
function findAllChordPositions(chordName: string, maxFret: number = 12): ChordShape[] {
  const chordPositions: ChordShape[] = [];
  const intervals = getChordIntervals(chordName);
  if (!intervals) return chordPositions;
  
  // Find root note index (e.g. "F (E Shape)" -> "F", "Cm7" -> "C")
  const rootNote = chordName.replace(/\s*\(.*\)$/, '').trim().match(/^([A-G#]+)/)?.[1];
  if (!rootNote) return chordPositions;
  const rootIndex = NOTES.indexOf(rootNote);
  
  // Try different starting positions on each string
  for (let startFret = 0; startFret <= maxFret; startFret++) {
    for (let rootString = 0; rootString < 6; rootString++) {
      const rootFret = startFret;
      const rootNoteAtFret = (STRING_TUNINGS[rootString] + rootFret) % 12;
      
      if (rootNoteAtFret === rootIndex) {
        // Found root note, try to build chord from this position
        const positions: Array<[number, number]> = [[-1, 0], [-1, 0], [-1, 0], [-1, 0], [-1, 0], [-1, 0]];
        let validChord = true;
        let fingerCount = 0;
        
        // Set root note
        positions[rootString] = [rootFret, 1];
        fingerCount++;
        
        // Try to find other chord tones
        for (let interval of intervals.slice(1)) {
          let found = false;
          
          // Look for this interval on other strings
          for (let stringIdx = 0; stringIdx < 6; stringIdx++) {
            if (stringIdx === rootString) continue;
            
            for (let fret = 0; fret <= maxFret; fret++) {
              const noteAtFret = (STRING_TUNINGS[stringIdx] + fret) % 12;
              const targetNote = (rootIndex + interval) % 12;
              
              if (noteAtFret === targetNote && positions[stringIdx][0] === -1) {
                positions[stringIdx] = [fret, fingerCount + 1];
                fingerCount++;
                found = true;
                break;
              }
            }
            if (found) break;
          }
          
          if (!found) {
            validChord = false;
            break;
          }
        }
        
        if (validChord && fingerCount >= 3) {
          chordPositions.push({
            name: chordName,
            positions: positions
          });
        }
      }
    }
  }
  
  return chordPositions;
}

export type HighlightNote = { stringIdx: number; fret: number; note: string; label?: string };

function Fretboard({ 
  scaleNotes, 
  rootNote, 
  mode, 
  chord, 
  chordDisplay,
  allChordPositions,
  chordPositionMode,
  chordName,
  highlightNote
}: { 
  scaleNotes: string[], 
  rootNote: string, 
  mode: Mode,
  chord?: ChordShape,
  chordDisplay: ChordDisplay,
  allChordPositions?: ChordShape[],
  chordPositionMode?: ChordPositionMode,
  chordName?: string,
  highlightNote?: HighlightNote
}) {
  const audioContextRef = useRef<AudioContext | null>(null);

  useEffect(() => {
    // Initialize audio context
    audioContextRef.current = new (window.AudioContext || (window as any).webkitAudioContext)();
    
    return () => {
      if (audioContextRef.current) {
        audioContextRef.current.close();
      }
    };
  }, []);

  const playGuitarNote = (stringIdx: number, guitarFret: number) => {
    if (!audioContextRef.current) return;
    const context = audioContextRef.current;
    playGuitarStringFret(context, stringIdx, guitarFret, context.currentTime, { peakGain: 0.29 });
  };

  const playChord = (chord: ChordShape) => {
    if (!audioContextRef.current) return;
    if (!chord) return;
    const context = audioContextRef.current;
    const now = context.currentTime;
    // Strum down: high strings slightly earlier (index 0 = high E)
    chord.positions.forEach(([fret], stringIdx) => {
      if (fret === -1) return;
      const stagger = stringIdx * 0.011;
      playGuitarStringFret(context, stringIdx, fret, now + stagger, {
        peakGain: 0.22,
        durationScale: 0.92,
      });
    });
  };

  return (
    <div className="fretboard-container">
      <div className="fretboard">
        {/* Fret numbers */}
        <div className="fret-numbers">
          <div className="fret-number open-fret">0</div>
          {[...Array(NUM_FRETS)].map((_, i) => (
            <div key={i} className="fret-number">{i + 1}</div>
          ))}
        </div>
        
        {/* Strings and frets */}
        {STRING_TUNINGS.map((openNote, stringIdx) => {
          const openToneType = mode === 'chord' && chordPositionMode === 'all-positions' && chordName ? getChordToneType(openNote, chordName) : null;
          const openLabel = openToneType
            ? (chordDisplay === 'notes' ? NOTES[openNote] : (openToneType === 'root' ? 'R' : openToneType === '3rd' ? '3' : openToneType === '5th' ? '5' : '7'))
            : NOTES[openNote];
          const isHighlightOpen = highlightNote && highlightNote.stringIdx === stringIdx && highlightNote.fret === 0;
          return (
          <div className="string-row" key={stringIdx}>
            {/* Open string note */}
            <div 
              className={`open-string clickable ${
                mode === 'scale' && scaleNotes.includes(NOTES[openNote]) ? 'in-scale' : ''
              } ${
                mode === 'scale' && NOTES[openNote] === rootNote ? 'root-note' : ''
              } ${
                openToneType ? `chord-tone-open chord-tone-${openToneType}` : ''
              } ${isHighlightOpen ? 'highlight-drill' : ''}`}
              onClick={() => playGuitarNote(stringIdx, 0)}
            >
              {isHighlightOpen ? (highlightNote!.label ?? highlightNote!.note) : openLabel}
            </div>
            
            {/* Frets */}
                          {[...Array(NUM_FRETS)].map((_, fret) => {
                const noteIndex = (openNote + fret + 1) % 12;
                const note = NOTES[noteIndex];
                const isHighlightFret = highlightNote && highlightNote.stringIdx === stringIdx && highlightNote.fret === fret + 1;
                
                if (isHighlightFret) {
                  return (
                    <div key={fret} className="fret-container">
                      <div className="string-line"></div>
                      <div className="note-circle highlight-drill">
                        <span className="note-text">{highlightNote!.label ?? highlightNote!.note}</span>
                      </div>
                    </div>
                  );
                }
                
                if (mode === 'scale') {
                  const isInScale = scaleNotes.includes(note);
                  const isRoot = note === rootNote;
                  
                  return (
                    <div key={fret} className="fret-container">
                      <div className="string-line"></div>
                      {isInScale && (
                        <div 
                          className={`note-circle ${isRoot ? 'root-note' : ''}`}
                          onClick={() => playGuitarNote(stringIdx, fret + 1)}
                        >
                          <span className="note-text">{note}</span>
                        </div>
                      )}
                    </div>
                  );
              } else {
                // Chord mode
                if (chordPositionMode === 'all-positions' && chordName) {
                  // Show every position where this note is a chord tone (all C, E, G etc. up the neck)
                  const chordToneType = getChordToneType(noteIndex, chordName);
                  const isChordTone = chordToneType !== null;
                  const toneLabel = chordToneType === 'root' ? 'R' : chordToneType === '3rd' ? '3' : chordToneType === '5th' ? '5' : chordToneType === '7th' ? '7' : null;
                  const displayLabel = chordDisplay === 'notes' ? note : (toneLabel ?? note);
                  const toneClass = chordToneType ? `chord-tone-${chordToneType}` : '';
                  return (
                    <div key={fret} className="fret-container">
                      <div className="string-line"></div>
                      {isChordTone && (
                        <div 
                          className={`note-circle chord-position ${toneClass}`}
                          onClick={() => playGuitarNote(stringIdx, fret + 1)}
                        >
                          <span className="note-text">{displayLabel}</span>
                        </div>
                      )}
                    </div>
                  );
                } else {
                  // Single chord mode
                  const chordPosition = chord?.positions[stringIdx];
                  const [chordFret, finger] = chordPosition || [-1, 0];
                  const isChordNote = chordFret === fret + 1;
                  const isOpenString = chordFret === 0 && fret === 0;
                  const isDontPlay = chordFret === -1;
                  
                  return (
                    <div key={fret} className="fret-container">
                      <div className={`string-line ${isDontPlay && fret === 0 ? 'dont-play' : ''}`}>
                        {isDontPlay && fret === 0 && <span className="x-mark">×</span>}
                      </div>
                      {(isChordNote || (isOpenString && fret === 0)) && (
                        <div 
                          className={`note-circle chord-note ${isOpenString ? 'open-note' : ''}`}
                          onClick={() => {
                            if (isOpenString) {
                              playGuitarNote(stringIdx, 0);
                            } else {
                              if (chord) playChord(chord);
                            }
                          }}
                        >
                          <span className="note-text">
                            {chordDisplay === 'notes' 
                              ? (isOpenString ? NOTES[openNote] : note)
                              : (isOpenString ? 'O' : finger)
                            }
                          </span>
                        </div>
                      )}
                    </div>
                  );
                }
              }
            })}
          </div>
          );
        })}
        
        {/* Fret markers */}
        <div className="fret-markers">
          <div className="fret-marker-spacer"></div>
          {[...Array(NUM_FRETS)].map((_, i) => (
            <div key={i} className="fret-marker-container">
              {FRET_MARKERS.includes(i + 1) && i + 1 !== DOUBLE_FRET_MARKER && (
                <div className="fret-marker single"></div>
              )}
              {i + 1 === DOUBLE_FRET_MARKER && (
                <>
                  <div className="fret-marker double top"></div>
                  <div className="fret-marker double bottom"></div>
                </>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function Metronome() {
  const [bpm, setBpm] = useState(120);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentBeat, setCurrentBeat] = useState(0);
  const [timeSignature, setTimeSignature] = useState('4/4');
  const [beatEmphasis, setBeatEmphasis] = useState<number[]>([3, 1, 2, 1]); // 0-3 emphasis levels
  
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);

  const timeSignatures: Record<string, number> = {
    '4/4': 4,
    '3/4': 3,
    '6/8': 6,
    '5/4': 5,
    '7/8': 7,
  };

  const beatsPerMeasure = timeSignatures[timeSignature];

  useEffect(() => {
    // Initialize audio context
    audioContextRef.current = new (window.AudioContext || (window as any).webkitAudioContext)();
    
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
      if (audioContextRef.current) {
        audioContextRef.current.close();
      }
    };
  }, []);

  useEffect(() => {
    // Update beat emphasis array when time signature changes
    const newEmphasis = Array(beatsPerMeasure).fill(1);
    newEmphasis[0] = 3; // First beat always emphasized
    setBeatEmphasis(newEmphasis);
    setCurrentBeat(0);
  }, [timeSignature, beatsPerMeasure]);

  const playClick = (emphasis: number) => {
    if (!audioContextRef.current) return;
    
    const audioContext = audioContextRef.current;
    const now = audioContext.currentTime;
    
    // Create a more complex sound using multiple oscillators for a wooden click
    const clickDuration = 0.015; // Shorter for more click-like sound
    
    // Main click component - triangle wave for softer sound
    const osc1 = audioContext.createOscillator();
    const osc2 = audioContext.createOscillator();
    const noiseBuffer = createNoiseBuffer(audioContext);
    const noiseSource = audioContext.createBufferSource();
    noiseSource.buffer = noiseBuffer;
    
    // Filters for shaping the sound
    const highpass = audioContext.createBiquadFilter();
    highpass.type = 'highpass';
    highpass.frequency.value = 200; // Remove low rumble
    
    const bandpass = audioContext.createBiquadFilter();
    bandpass.type = 'bandpass';
    bandpass.Q.value = 0.5;
    
    // Gain nodes
    const osc1Gain = audioContext.createGain();
    const osc2Gain = audioContext.createGain();
    const noiseGain = audioContext.createGain();
    const masterGain = audioContext.createGain();
    
    // Different settings for emphasis levels
    const settings = [
      // Silent
      { freq1: 0, freq2: 0, bandFreq: 0, volume: 0 },
      // Soft tick
      { freq1: 800, freq2: 1600, bandFreq: 3000, volume: 0.15 },
      // Medium tock
      { freq1: 600, freq2: 1200, bandFreq: 2500, volume: 0.25 },
      // Strong tick (downbeat)
      { freq1: 1000, freq2: 2000, bandFreq: 4000, volume: 0.4 }
    ];
    
    const setting = settings[emphasis];
    
    if (setting.volume === 0) return; // Don't play if silent
    
    // Configure oscillators
    osc1.type = 'triangle';
    osc1.frequency.value = setting.freq1;
    osc2.type = 'sine';
    osc2.frequency.value = setting.freq2;
    
    bandpass.frequency.value = setting.bandFreq;
    
    // Set initial gains
    osc1Gain.gain.value = 0.6;
    osc2Gain.gain.value = 0.3;
    noiseGain.gain.value = 0.1; // Subtle noise for texture
    masterGain.gain.value = setting.volume;
    
    // Create sharp attack envelope
    const attackTime = 0.001;
    
    // Apply envelope to all components
    [osc1Gain, osc2Gain, noiseGain].forEach(gain => {
      gain.gain.setValueAtTime(0, now);
      gain.gain.linearRampToValueAtTime(gain.gain.value, now + attackTime);
      gain.gain.exponentialRampToValueAtTime(0.001, now + clickDuration);
    });
    
    // Connect the audio graph
    osc1.connect(osc1Gain);
    osc2.connect(osc2Gain);
    noiseSource.connect(noiseGain);
    
    osc1Gain.connect(highpass);
    osc2Gain.connect(highpass);
    noiseGain.connect(bandpass);
    
    highpass.connect(masterGain);
    bandpass.connect(masterGain);
    masterGain.connect(audioContext.destination);
    
    // Start and stop
    osc1.start(now);
    osc2.start(now);
    noiseSource.start(now);
    
    osc1.stop(now + clickDuration + 0.01);
    osc2.stop(now + clickDuration + 0.01);
    noiseSource.stop(now + clickDuration + 0.01);
  };
  
  // Helper function to create white noise for texture
  const createNoiseBuffer = (audioContext: AudioContext) => {
    const bufferSize = audioContext.sampleRate * 0.02; // 20ms buffer
    const buffer = audioContext.createBuffer(1, bufferSize, audioContext.sampleRate);
    const output = buffer.getChannelData(0);
    
    for (let i = 0; i < bufferSize; i++) {
      output[i] = Math.random() * 2 - 1;
    }
    
    return buffer;
  };

  const startStop = () => {
    if (isPlaying) {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
      setIsPlaying(false);
      setCurrentBeat(0);
    } else {
      setIsPlaying(true);
      const interval = 60000 / bpm; // milliseconds per beat
      
      // Play first beat immediately
      playClick(beatEmphasis[0]);
      setCurrentBeat(0);
      
      let beat = 1;
      intervalRef.current = setInterval(() => {
        playClick(beatEmphasis[beat]);
        setCurrentBeat(beat);
        beat = (beat + 1) % beatsPerMeasure;
      }, interval);
    }
  };

  const updateEmphasis = (index: number, value: number) => {
    const newEmphasis = [...beatEmphasis];
    newEmphasis[index] = value;
    setBeatEmphasis(newEmphasis);
  };

  const handleBpmChange = (newBpm: number) => {
    setBpm(newBpm);
    if (isPlaying) {
      // Restart metronome with new BPM
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
      setIsPlaying(false);
      setTimeout(() => startStop(), 50);
    }
  };

  return (
    <div className="metronome-container">
      <h2>Metronome</h2>
      
      <div className="metronome-controls">
        <div className="bpm-control">
          <label>BPM</label>
          <input 
            type="range" 
            min="40" 
            max="240" 
            value={bpm}
            onChange={e => handleBpmChange(Number(e.target.value))}
            className="bpm-slider"
          />
          <input 
            type="number" 
            min="40" 
            max="240" 
            value={bpm}
            onChange={e => handleBpmChange(Number(e.target.value))}
            className="bpm-input"
          />
        </div>

        <div className="time-signature-control">
          <label>Time Signature</label>
          <select 
            value={timeSignature} 
            onChange={(e) => setTimeSignature(e.target.value)}
            disabled={isPlaying}
          >
            {Object.keys(timeSignatures).map(sig => (
              <option key={sig} value={sig}>{sig}</option>
            ))}
          </select>
        </div>

        <button 
          className={`play-button ${isPlaying ? 'playing' : ''}`}
          onClick={startStop}
        >
          {isPlaying ? 'Stop' : 'Play'}
        </button>
      </div>

      <div className="beat-emphasis">
        <label>Beat Emphasis</label>
        <div className="beats">
          {beatEmphasis.slice(0, beatsPerMeasure).map((emphasis, index) => (
            <div key={index} className="beat-control">
              <div className={`beat-display ${currentBeat === index ? 'active' : ''}`}>
                {index + 1}
              </div>
              <div className="emphasis-control">
                <button 
                  className="emphasis-btn decrease"
                  onClick={() => !isPlaying && emphasis > 0 && updateEmphasis(index, emphasis - 1)}
                  disabled={isPlaying || emphasis === 0}
                >
                  −
                </button>
                <div className={`emphasis-level level-${emphasis}`}>
                  <div className="emphasis-fill" style={{ height: `${(emphasis + 1) * 25}%` }} />
                </div>
                <button 
                  className="emphasis-btn increase"
                  onClick={() => !isPlaying && emphasis < 3 && updateEmphasis(index, emphasis + 1)}
                  disabled={isPlaying || emphasis === 3}
                >
                  +
                </button>
              </div>
            </div>
          ))}
        </div>
        <div className="emphasis-legend">
          <span className="legend-item">
            <span className="legend-color level-0"></span>Silent
          </span>
          <span className="legend-item">
            <span className="legend-color level-1"></span>Soft
          </span>
          <span className="legend-item">
            <span className="legend-color level-2"></span>Medium
          </span>
          <span className="legend-item">
            <span className="legend-color level-3"></span>Strong
          </span>
        </div>
      </div>
    </div>
  );
}

// Strum patterns: D = Down, U = Up, - = Rest
const COMMON_STRUM_PATTERNS = [
  { name: 'On The One', pattern: ['D', '-', '-', '-', '-', '-', '-', '-'] },
  { name: 'On The Beats', pattern: ['D', '-', 'D', '-', 'D', '-', 'D', '-'] },
  { name: 'Old Faithful', pattern: ['D', '-', 'D', 'U', '-', 'U', 'D', 'U'] },
  { name: 'And After 1', pattern: ['D', 'U', '-', '-', 'D', 'U', 'D', 'U'] },
  { name: 'And After Two', pattern: ['D', '-', 'D', 'U', 'D', 'U', 'D', 'U'] },
  { name: 'And After Three', pattern: ['D', '-', 'D', '-', 'D', 'U', 'D', 'U'] },
  { name: 'And After Four', pattern: ['D', '-', 'D', '-', 'D', '-', 'D', 'U'] },
  { name: 'Eight At The Start', pattern: ['D', 'U', 'D', 'U', 'D', 'U', 'D', 'U'] },
  { name: 'Eight In The Middle', pattern: ['D', '-', 'D', 'U', 'D', 'U', 'D', '-'] },
  { name: 'Eight At The End', pattern: ['D', '-', 'D', '-', 'D', '-', 'D', 'U'] },
  { name: 'Folk Strum', pattern: ['D', '-', 'D', 'U', 'D', 'U', 'D', 'U'] },
  { name: 'Country Strum', pattern: ['D', '-', 'D', '-', 'D', 'U', '-', 'U'] },
];

function playStrumTick(audioContextRef: React.MutableRefObject<AudioContext | null>) {
  if (!audioContextRef.current) return;
  const ctx = audioContextRef.current;
  playNylonStrumClick(ctx, ctx.currentTime, 1);
}

// Common chord progressions by roman numerals
const CHORD_PROGRESSIONS = [
  { name: 'Custom', pattern: [] },
  { name: 'I-IV-V', pattern: ['I', 'IV', 'V'] },
  { name: 'I-V-vi-IV', pattern: ['I', 'V', 'vi', 'IV'] },
  { name: 'I-vi-IV-V', pattern: ['I', 'vi', 'IV', 'V'] },
  { name: 'I-IV-I-V', pattern: ['I', 'IV', 'I', 'V'] },
  { name: 'I-vi-ii-V', pattern: ['I', 'vi', 'ii', 'V'] },
  { name: 'ii-V-I (Jazz)', pattern: ['ii', 'V', 'I'] },
  { name: 'I-iii-IV-V', pattern: ['I', 'iii', 'IV', 'V'] },
  { name: 'vi-IV-I-V', pattern: ['vi', 'IV', 'I', 'V'] },
  { name: 'I-V-vi-iii-IV-I-IV-V', pattern: ['I', 'V', 'vi', 'iii', 'IV', 'I', 'IV', 'V'] },
  { name: 'I-IV-vi-V', pattern: ['I', 'IV', 'vi', 'V'] },
];

// Map roman numerals to scale degrees (0-indexed)
const SCALE_DEGREES: Record<string, number> = {
  'I': 0,
  'ii': 1,
  'iii': 2,
  'IV': 3,
  'V': 4,
  'vi': 5,
  'vii°': 6,
};

// Major scale chords: I, ii, iii, IV, V, vi, vii°
// Using available chords from Standard category
const getChordInKey = (key: string, degree: string): string => {
  const rootNotes = ['C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B'];
  const keyIndex = rootNotes.indexOf(key);
  const majorScaleIntervals = [0, 2, 4, 5, 7, 9, 11]; // Major scale semitones
  
  const degreeIndex = SCALE_DEGREES[degree];
  const noteIndex = (keyIndex + majorScaleIntervals[degreeIndex]) % 12;
  const chordRoot = rootNotes[noteIndex];
  
  // Determine chord quality based on scale degree
  if (degree === 'I' || degree === 'IV' || degree === 'V') {
    return chordRoot; // Major
  } else if (degree === 'ii' || degree === 'iii' || degree === 'vi') {
    return chordRoot + 'm'; // Minor
  } else if (degree === 'vii°') {
    return chordRoot + 'dim'; // Diminished (not in our basic set, use minor)
  }
  return chordRoot;
};

// Helper function to find a chord shape in categories, trying variations
const findChordShape = (chordName: string, category: ChordCategory): ChordShape | null => {
  // Try exact match first
  if (CHORD_CATEGORIES[category][chordName]) {
    return CHORD_CATEGORIES[category][chordName];
  }
  
  // Try in Standard category if not found
  if (category !== 'Standard' && CHORD_CATEGORIES['Standard'][chordName]) {
    return CHORD_CATEGORIES['Standard'][chordName];
  }
  
  // If it's a minor chord (ends with 'm'), try without the 'm' and see if major exists
  if (chordName.endsWith('m') && !chordName.endsWith('dim') && !chordName.endsWith('maj')) {
    const rootNote = chordName.slice(0, -1);
    // For minor chords, try to find them in Standard category
    if (CHORD_CATEGORIES['Standard'][chordName]) {
      return CHORD_CATEGORIES['Standard'][chordName];
    }
    // Fallback to major if minor doesn't exist
    if (CHORD_CATEGORIES['Standard'][rootNote]) {
      return CHORD_CATEGORIES['Standard'][rootNote];
    }
  }
  
  // Return null if nothing found
  return null;
};

function IntervalReferenceSection() {
  const [selectedInterval, setSelectedInterval] = useState<IntervalData | null>(null);
  const [direction, setDirection] = useState<'ascending' | 'descending'>('ascending');
  /** Direction for the open YouTube clip (matches the row you clicked, not the toggle after open). */
  const [youtubeClipDirection, setYoutubeClipDirection] = useState<'ascending' | 'descending' | null>(null);
  const [playingType, setPlayingType] = useState<'interval' | 'song' | null>(null);
  const [youtubePlaying, setYoutubePlaying] = useState(false);
  const audioContextRef = useRef<AudioContext | null>(null);
  const youtubePlayerRef = useRef<HTMLIFrameElement | null>(null);

  useEffect(() => {
    audioContextRef.current = new (window.AudioContext || (window as any).webkitAudioContext)();
    return () => {
      if (audioContextRef.current) {
        audioContextRef.current.close();
      }
    };
  }, []);

  const playInterval = async (interval: IntervalData, dir: 'ascending' | 'descending') => {
    if (!audioContextRef.current) return;
    setYoutubePlaying(false);
    setYoutubeClipDirection(null);
    setPlayingType('interval');
    const context = audioContextRef.current;
    await resumeAudioContext(context);
    const now = context.currentTime;
    const baseMidi = 69; // A4
    const delta = dir === 'ascending' ? interval.semitones : -interval.semitones;
    const otherMidi = baseMidi + delta;
    const lowMidi = Math.min(baseMidi, otherMidi);
    const highMidi = Math.max(baseMidi, otherMidi);
    const [firstMidi, secondMidi] = dir === 'ascending' ? [lowMidi, highMidi] : [highMidi, lowMidi];
    const p1 = pickStringFretForMidi(firstMidi);
    const p2 = pickStringFretForMidi(secondMidi);
    if (!p1 || !p2) {
      setPlayingType(null);
      return;
    }
    const pluckOpts = { peakGain: 0.28 as const, durationScale: 0.9 as const };
    playGuitarStringFret(context, p1.stringIdx, p1.fret, now, pluckOpts);
    playGuitarStringFret(context, p2.stringIdx, p2.fret, now + 0.82, pluckOpts);
    setTimeout(() => setPlayingType(null), 1800);
  };

  const playSongExample = (interval: IntervalData, dir: 'ascending' | 'descending') => {
    const songData = dir === 'ascending' ? interval.ascending : interval.descending;
    if (!songData.youtubeId) return;
    setYoutubeClipDirection(dir);
    setPlayingType('song');
    setYoutubePlaying(true);
    setSelectedInterval(interval);
  };

  const closeYoutubePlayer = () => {
    setYoutubePlaying(false);
    setYoutubeClipDirection(null);
    setPlayingType(null);
  };

  return (
    <div className="interval-reference-section">
      {youtubePlaying && selectedInterval && youtubeClipDirection && (
        <div className="youtube-player-overlay">
          <div className="youtube-player-container">
            <button
              className="close-youtube-btn"
              onClick={closeYoutubePlayer}
              aria-label="Close"
              title="Close"
            >
              <IconX className="close-youtube-btn__icon" />
            </button>
            <iframe
              key={`${selectedInterval.name}-${youtubeClipDirection}-${
                youtubeClipDirection === 'ascending'
                  ? selectedInterval.ascending.youtubeId
                  : selectedInterval.descending.youtubeId
              }`}
              ref={youtubePlayerRef}
              width="800"
              height="450"
              src={`https://www.youtube.com/embed/${
                youtubeClipDirection === 'ascending'
                  ? selectedInterval.ascending.youtubeId
                  : selectedInterval.descending.youtubeId
              }?start=${
                youtubeClipDirection === 'ascending'
                  ? selectedInterval.ascending.startTime ?? 0
                  : selectedInterval.descending.startTime ?? 0
              }&autoplay=1&mute=1&playsinline=1&rel=0`}
              title="Song Example"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              allowFullScreen
            />
            <div className="song-info-overlay">
              <strong className="song-info-overlay__title">
                {youtubeClipDirection === 'ascending' ? (
                  <IconArrowUp className="song-info-overlay__dir" />
                ) : (
                  <IconArrowDown className="song-info-overlay__dir" />
                )}
                {selectedInterval.name}
              </strong>
              <div>
                &quot;
                {youtubeClipDirection === 'ascending'
                  ? selectedInterval.ascending.song
                  : selectedInterval.descending.song}
                &quot;
              </div>
              <div>
                {youtubeClipDirection === 'ascending'
                  ? selectedInterval.ascending.artist
                  : selectedInterval.descending.artist}
              </div>
            </div>
          </div>
        </div>
      )}
      <div className="interval-reference-header">
        <p className="interval-reference-lead">
          Hear each interval and optional song clips. Clips may start muted so autoplay works; unmute in the player if needed.
        </p>
        <div className="direction-selector" role="group" aria-label="Melodic direction">
          <button
            type="button"
            className={`direction-btn ${direction === 'ascending' ? 'active' : ''}`}
            onClick={() => setDirection('ascending')}
          >
            <IconArrowUp className="direction-btn__glyph" />
            Ascending
          </button>
          <button
            type="button"
            className={`direction-btn ${direction === 'descending' ? 'active' : ''}`}
            onClick={() => setDirection('descending')}
          >
            <IconArrowDown className="direction-btn__glyph" />
            Descending
          </button>
        </div>
      </div>
      <div className="intervals-grid">
        {INTERVALS.map((interval) => {
          const isSelected = selectedInterval?.name === interval.name;
          const songData = direction === 'ascending' ? interval.ascending : interval.descending;
          return (
            <div key={interval.name} className={`interval-card ${isSelected ? 'selected' : ''}`}>
              <div className="interval-header">
                <h3>{interval.name}</h3>
                <span className="interval-semitones">{interval.semitones} semitones</span>
              </div>
              <p className="interval-description">{interval.description}</p>
              <div className="interval-actions">
                <button
                  className={`play-interval-btn ${playingType === 'interval' && isSelected ? 'playing' : ''}`}
                  onClick={() => {
                    setSelectedInterval(interval);
                    void playInterval(interval, direction);
                  }}
                  disabled={playingType !== null || youtubePlaying}
                >
                  <span className="ear-btn__row">
                    <IconVolume className="ear-btn__icon" />
                    Hear interval
                  </span>
                </button>
                <div className="song-reference">
                  <div className="song-info">
                    <strong className="song-info__label">
                      {direction === 'ascending' ? (
                        <IconArrowUp className="song-info__dir" />
                      ) : (
                        <IconArrowDown className="song-info__dir" />
                      )}
                      Reference
                    </strong>
                    <div className="song-title">&ldquo;{songData.song}&rdquo;</div>
                    <div className="song-artist">{songData.artist}</div>
                  </div>
                  <button
                    className="listen-song-btn"
                    onClick={() => playSongExample(interval, direction)}
                    disabled={youtubePlaying}
                    title="Play song clip"
                  >
                    <span className="ear-btn__row">
                      <IconFilm className="ear-btn__icon" />
                      Clip
                    </span>
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/** Map chord intervals from root to string/frets so we can use the same guitar samples as the fretboard. */
function resolveChordVoicingForSamples(
  intervalsFromRoot: readonly number[],
  maxFret = 22
): Array<{ stringIdx: number; fret: number }> | null {
  const stringsDistinct = (positions: Array<{ stringIdx: number; fret: number }>) => {
    const seen = new Set<number>();
    for (const p of positions) {
      if (seen.has(p.stringIdx)) return false;
      seen.add(p.stringIdx);
    }
    return true;
  };

  for (let rootMidi = 33; rootMidi <= 72; rootMidi++) {
    const positions: Array<{ stringIdx: number; fret: number }> = [];
    let ok = true;
    for (const iv of intervalsFromRoot) {
      const p = pickStringFretForMidi(rootMidi + iv, { maxFret });
      if (!p) {
        ok = false;
        break;
      }
      positions.push(p);
    }
    if (ok && stringsDistinct(positions)) return positions;
  }
  return null;
}

function EarTraining() {
  const [quizMode, setQuizMode] = useState<'interval' | 'chord'>('interval');
  /** Endless = open-ended random questions; Quiz = fixed length with score. */
  const [sessionKind, setSessionKind] = useState<'endless' | 'quiz'>('endless');
  const [level, setLevel] = useState<'beginner' | 'intermediate' | 'advanced' | 'all'>('beginner');
  const [quizRunLength, setQuizRunLength] = useState(10);
  const [quizRunActive, setQuizRunActive] = useState(false);
  const [quizRunIndex, setQuizRunIndex] = useState(0);
  const [quizRunCorrect, setQuizRunCorrect] = useState(0);
  const [quizRunStats, setQuizRunStats] = useState<Record<string, { correct: number; total: number }>>({});
  const [quizShowSummary, setQuizShowSummary] = useState(false);
  const [currentPrompt, setCurrentPrompt] = useState<{
    label: string;
    semitones?: number;
    intervals?: number[];
  } | null>(null);
  const [feedback, setFeedback] = useState<string | null>(null);
  const [reveal, setReveal] = useState(false);
  const [showIntervalReference, setShowIntervalReference] = useState(false);
  const audioContextRef = useRef<AudioContext | null>(null);

  type IntervalOption = { label: string; semitones: number };
  type ChordOption = { label: string; intervals: number[] };

  const intervalOptions: IntervalOption[] = [
    { label: 'Minor 2nd', semitones: 1 },
    { label: 'Major 2nd', semitones: 2 },
    { label: 'Minor 3rd', semitones: 3 },
    { label: 'Major 3rd', semitones: 4 },
    { label: 'Perfect 4th', semitones: 5 },
    { label: 'Tritone', semitones: 6 },
    { label: 'Perfect 5th', semitones: 7 },
    { label: 'Minor 6th', semitones: 8 },
    { label: 'Major 6th', semitones: 9 },
    { label: 'Minor 7th', semitones: 10 },
    { label: 'Major 7th', semitones: 11 },
    { label: 'Octave', semitones: 12 },
  ];

  const chordOptions: ChordOption[] = [
    { label: 'Major', intervals: [0, 4, 7] },
    { label: 'Minor', intervals: [0, 3, 7] },
    { label: 'Diminished', intervals: [0, 3, 6] },
    { label: 'Augmented', intervals: [0, 4, 8] },
  ];

  useEffect(() => {
    audioContextRef.current = new (window.AudioContext || (window as any).webkitAudioContext)();
    return () => {
      if (audioContextRef.current) {
        audioContextRef.current.close();
      }
    };
  }, []);

  const playIntervalPrompt = async (semitones: number) => {
    if (!audioContextRef.current) return;
    const context = audioContextRef.current;
    await resumeAudioContext(context);
    const now = context.currentTime;
    const baseMidi = 69;
    const secondMidi = baseMidi + semitones;
    const p1 = pickStringFretForMidi(baseMidi);
    const p2 = pickStringFretForMidi(secondMidi);
    if (!p1 || !p2) return;
    const pluckOpts = { peakGain: 0.28 as const, durationScale: 0.9 as const };
    playGuitarStringFret(context, p1.stringIdx, p1.fret, now, pluckOpts);
    playGuitarStringFret(context, p2.stringIdx, p2.fret, now + 0.85, pluckOpts);
  };

  const playChordPrompt = async (intervals: number[]) => {
    if (!audioContextRef.current) return;
    const context = audioContextRef.current;
    await resumeAudioContext(context);
    const positions = resolveChordVoicingForSamples(intervals);
    if (!positions?.length) return;
    const now = context.currentTime;
    const pluckOpts = { peakGain: 0.22 as const, durationScale: 0.92 as const };
    const sorted = [...positions].sort((a, b) => a.stringIdx - b.stringIdx);
    sorted.forEach((pos, idx) => {
      const stagger = idx * 0.012;
      playGuitarStringFret(context, pos.stringIdx, pos.fret, now + stagger, pluckOpts);
    });
  };

  const getLevelFilteredIntervals = () => {
    if (level === 'all') return intervalOptions;
    if (level === 'beginner') return intervalOptions.filter(opt => opt.semitones <= 5);
    if (level === 'intermediate') return intervalOptions.filter(opt => opt.semitones >= 3 && opt.semitones <= 8);
    return intervalOptions.filter(opt => opt.semitones >= 6);
  };

  const startNewQuestion = async () => {
    setFeedback(null);
    setReveal(false);
    if (!audioContextRef.current) return;
    await resumeAudioContext(audioContextRef.current);
    if (quizMode === 'interval') {
      const pool = getLevelFilteredIntervals();
      if (!pool.length) return;
      const choice = pool[Math.floor(Math.random() * pool.length)];
      setCurrentPrompt({ label: choice.label, semitones: choice.semitones });
      await playIntervalPrompt(choice.semitones);
      return;
    }
    const choice = chordOptions[Math.floor(Math.random() * chordOptions.length)];
    setCurrentPrompt({ label: choice.label, intervals: choice.intervals });
    await playChordPrompt(choice.intervals);
  };

  const handlePlayClick = async () => {
    if (!audioContextRef.current) return;
    await resumeAudioContext(audioContextRef.current);
    if (currentPrompt) {
      if (quizMode === 'interval' && currentPrompt.semitones !== undefined) {
        await playIntervalPrompt(currentPrompt.semitones);
      } else if (quizMode === 'chord' && currentPrompt.intervals) {
        await playChordPrompt(currentPrompt.intervals);
      }
    } else {
      await startNewQuestion();
    }
  };

  const handleGuess = (guess: string) => {
    if (!currentPrompt) return;
    const isCorrect = guess === currentPrompt.label;
    setFeedback(isCorrect ? 'Correct!' : 'Not quite. Try again or reveal the answer.');
    setReveal(true);

    if (sessionKind === 'quiz' && quizRunActive) {
      setQuizRunStats(prev => {
        const current = prev[currentPrompt.label] || { correct: 0, total: 0 };
        return {
          ...prev,
          [currentPrompt.label]: {
            correct: current.correct + (isCorrect ? 1 : 0),
            total: current.total + 1,
          },
        };
      });
      setQuizRunCorrect(prev => prev + (isCorrect ? 1 : 0));
    }
  };

  const startScoredQuiz = async () => {
    setQuizShowSummary(false);
    setQuizRunActive(true);
    setQuizRunIndex(0);
    setQuizRunCorrect(0);
    setQuizRunStats({});
    setFeedback(null);
    setReveal(false);
    await startNewQuestion();
  };

  const nextQuizQuestion = async () => {
    if (!quizRunActive) return;
    if (quizRunIndex + 1 >= quizRunLength) {
      setQuizRunActive(false);
      setQuizShowSummary(true);
      return;
    }
    setQuizRunIndex((prev) => prev + 1);
    await startNewQuestion();
  };

  const getRecommendations = () => {
    const entries = Object.entries(quizRunStats)
      .map(([label, data]) => ({
        label,
        accuracy: data.total ? Math.round((data.correct / data.total) * 100) : 0,
      }))
      .sort((a, b) => a.accuracy - b.accuracy);
    return entries.slice(0, 3);
  };

  const showGuessGrid =
    sessionKind === 'endless' || (sessionKind === 'quiz' && quizRunActive);

  const switchQuizMode = (mode: 'interval' | 'chord') => {
    setQuizMode(mode);
    setQuizRunActive(false);
    setQuizShowSummary(false);
    setCurrentPrompt(null);
    setFeedback(null);
    setReveal(false);
  };

  return (
    <div className="ear-training-container">
      <div className="ear-training-header">
        <div>
          <h2>Interval Practice</h2>
          <p className="ear-training-description">
            Endless random drills are ready as soon as you open this tab. Use scored quizzes when you want a clear
            target and a final score.
          </p>
        </div>
        <div className="ear-training-modes">
          <button
            type="button"
            className={`mode-chip ${quizMode === 'interval' ? 'active' : ''}`}
            onClick={() => switchQuizMode('interval')}
          >
            Intervals
          </button>
          <button
            type="button"
            className={`mode-chip ${quizMode === 'chord' ? 'active' : ''}`}
            onClick={() => switchQuizMode('chord')}
          >
            Chord qualities
          </button>
        </div>
      </div>

      <div className="quiz-card">
        <div className="quiz-toolbar">
          <div className="quiz-toggle">
            <button
              type="button"
              className={`mode-chip ${sessionKind === 'endless' ? 'active' : ''}`}
              onClick={() => {
                setSessionKind('endless');
                setQuizRunActive(false);
                setQuizShowSummary(false);
              }}
            >
              Endless
            </button>
            <button
              type="button"
              className={`mode-chip ${sessionKind === 'quiz' ? 'active' : ''}`}
              onClick={() => {
                setSessionKind('quiz');
                setQuizRunActive(false);
                setQuizShowSummary(false);
              }}
            >
              Scored quiz
            </button>
          </div>
          {sessionKind === 'quiz' && !quizRunActive && (
            <div className="quiz-run-setup">
              <label className="quiz-run-setup__label">
                Questions
                <select
                  value={quizRunLength}
                  onChange={(e) => setQuizRunLength(Number(e.target.value))}
                  className="quiz-run-setup__select"
                >
                  {[5, 10, 15, 20].map((n) => (
                    <option key={n} value={n}>
                      {n}
                    </option>
                  ))}
                </select>
              </label>
              <button type="button" className="ear-toolbar-btn ear-toolbar-btn--accent" onClick={() => void startScoredQuiz()}>
                <IconZap className="ear-toolbar-btn__icon" />
                Start quiz
              </button>
            </div>
          )}
          {quizMode === 'interval' && (
            <div className="level-select">
              <label>
                Level:
                <select value={level} onChange={(e) => setLevel(e.target.value as typeof level)}>
                  <option value="beginner">Beginner (m2–P4)</option>
                  <option value="intermediate">Intermediate (m3–m6)</option>
                  <option value="advanced">Advanced (TT+)</option>
                  <option value="all">All</option>
                </select>
              </label>
            </div>
          )}
        </div>

        {quizRunActive && (
          <div className="quiz-run-progress" role="status">
            Question <strong>{quizRunIndex + 1}</strong> of <strong>{quizRunLength}</strong>
            <span className="quiz-run-progress__sep" aria-hidden="true">
              ·
            </span>
            Correct so far: <strong>{quizRunCorrect}</strong>
          </div>
        )}

        <div className="quiz-actions">
          <button type="button" className="ear-toolbar-btn ear-toolbar-btn--primary" onClick={() => void handlePlayClick()}>
            <IconVolume className="ear-toolbar-btn__icon" />
            Play
          </button>
          <button type="button" className="ear-toolbar-btn ear-toolbar-btn--secondary" onClick={() => void startNewQuestion()}>
            <IconRefresh className="ear-toolbar-btn__icon" />
            New
          </button>
          {sessionKind === 'quiz' && quizRunActive && (
            <button type="button" className="ear-toolbar-btn ear-toolbar-btn--secondary" onClick={() => void nextQuizQuestion()}>
              Next question ({quizRunIndex + 1}/{quizRunLength})
            </button>
          )}
        </div>

        {showGuessGrid && (
          <div className="quiz-options">
            {(quizMode === 'interval' ? getLevelFilteredIntervals() : chordOptions).map((option) => (
              <button key={option.label} type="button" className="quiz-option" onClick={() => handleGuess(option.label)}>
                {option.label}
              </button>
            ))}
          </div>
        )}

        {sessionKind === 'quiz' && !quizRunActive && !quizShowSummary && (
          <p className="quiz-idle-hint">
            Choose how many questions you want, then <strong>Start quiz</strong>. Use <strong>Play</strong> anytime to
            hear the current clip again (or to draw the first question in endless mode).
          </p>
        )}

        <div className="quiz-feedback">
          {feedback && <span className="feedback-text">{feedback}</span>}
          {reveal && currentPrompt && <span className="answer-text">Answer: {currentPrompt.label}</span>}
        </div>

        {sessionKind === 'quiz' && quizShowSummary && (
          <div className="quiz-summary">
            <div className="summary-score">
              Final score: {quizRunCorrect}/{quizRunLength} ({Math.round((quizRunCorrect / quizRunLength) * 100)}%)
            </div>
            <div className="summary-recommendations">
              <strong>Recommendations:</strong>
              {getRecommendations().length === 0 ? (
                <span className="feedback-text">Keep practicing for more insights.</span>
              ) : (
                <ul>
                  {getRecommendations().map((item) => (
                    <li key={item.label}>
                      Focus on {item.label} ({item.accuracy}% accuracy)
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        )}
      </div>

      <div className="interval-reference-toggle-wrap">
        <button
          type="button"
          className={`interval-reference-toggle-btn ${showIntervalReference ? 'open' : ''}`}
          onClick={() => setShowIntervalReference((v) => !v)}
          aria-expanded={showIntervalReference}
        >
          {showIntervalReference ? (
            <IconChevronDown className="interval-reference-toggle-btn__chev" />
          ) : (
            <IconChevronRight className="interval-reference-toggle-btn__chev" />
          )}
          <span className="interval-reference-toggle-btn__text">
            {showIntervalReference ? 'Hide interval library' : 'Interval library & song examples'}
          </span>
        </button>
      </div>
      {showIntervalReference && (
        <div className="interval-reference-wrap">
          <IntervalReferenceSection />
        </div>
      )}
    </div>
  );
}

// --- Memorization games (curated fretboard drills) ---
type NoteDrillMode = 'single-string' | 'single-note' | 'all-random' | 'region-low' | 'region-mid' | 'region-high' | 'naturals-only';

type StringSet = 'all' | 'high-e' | 'top-2' | 'top-3' | 'bottom-2' | 'bottom-3' | 'low-e';

function getStringSetIndices(stringSet: StringSet): number[] {
  switch (stringSet) {
    case 'all': return [0, 1, 2, 3, 4, 5];
    case 'high-e': return [0];
    case 'top-2': return [0, 1];
    case 'top-3': return [0, 1, 2];
    case 'bottom-2': return [4, 5];
    case 'bottom-3': return [3, 4, 5];
    case 'low-e': return [5];
    default: return [0, 1, 2, 3, 4, 5];
  }
}

function filterPoolByStringSet(pool: Array<{ stringIdx: number; fret: number }>, stringSet: StringSet): Array<{ stringIdx: number; fret: number }> {
  if (stringSet === 'all') return pool;
  const allowed = new Set(getStringSetIndices(stringSet));
  return pool.filter((p) => allowed.has(p.stringIdx));
}

const STRING_NAMES = ['1 (high E)', '2 (B)', '3 (G)', '4 (D)', '5 (A)', '6 (low E)'];

function getNoteAtPosition(stringIdx: number, fret: number): string {
  const noteIndex = (STRING_TUNINGS[stringIdx] + fret) % 12;
  return NOTES[noteIndex];
}

function buildNoteDrillPool(mode: NoteDrillMode, selectedString: number, selectedNote: string): Array<{ stringIdx: number; fret: number }> {
  const pool: Array<{ stringIdx: number; fret: number }> = [];
  const maxFret = 12;

  if (mode === 'single-string') {
    for (let fret = 0; fret <= maxFret; fret++) {
      pool.push({ stringIdx: selectedString, fret });
    }
    return pool;
  }

  if (mode === 'single-note') {
    const noteIndex = NOTES.indexOf(selectedNote);
    if (noteIndex === -1) return pool;
    for (let stringIdx = 0; stringIdx < 6; stringIdx++) {
      for (let fret = 0; fret <= maxFret; fret++) {
        if ((STRING_TUNINGS[stringIdx] + fret) % 12 === noteIndex) {
          pool.push({ stringIdx, fret });
        }
      }
    }
    return pool;
  }

  if (mode === 'region-low') {
    for (let stringIdx = 0; stringIdx < 6; stringIdx++) {
      for (let fret = 0; fret <= 4; fret++) pool.push({ stringIdx, fret });
    }
    return pool;
  }
  if (mode === 'region-mid') {
    for (let stringIdx = 0; stringIdx < 6; stringIdx++) {
      for (let fret = 5; fret <= 9; fret++) pool.push({ stringIdx, fret });
    }
    return pool;
  }
  if (mode === 'region-high') {
    for (let stringIdx = 0; stringIdx < 6; stringIdx++) {
      for (let fret = 10; fret <= maxFret; fret++) pool.push({ stringIdx, fret });
    }
    return pool;
  }

  if (mode === 'naturals-only') {
    const naturalNotes = ['C', 'D', 'E', 'F', 'G', 'A', 'B'];
    for (let stringIdx = 0; stringIdx < 6; stringIdx++) {
      for (let fret = 0; fret <= maxFret; fret++) {
        const note = getNoteAtPosition(stringIdx, fret);
        if (naturalNotes.includes(note)) pool.push({ stringIdx, fret });
      }
    }
    return pool;
  }

  // all-random
  for (let stringIdx = 0; stringIdx < 6; stringIdx++) {
    for (let fret = 0; fret <= maxFret; fret++) {
      pool.push({ stringIdx, fret });
    }
  }
  return pool;
}

/** All positions for one note, sorted low to high pitch (for "same note up & down" mode). */
function buildSameNoteSequence(note: string, stringSet: StringSet): Array<{ stringIdx: number; fret: number }> {
  const noteIndex = NOTES.indexOf(note);
  if (noteIndex === -1) return [];
  const positions: Array<{ stringIdx: number; fret: number }> = [];
  for (let stringIdx = 0; stringIdx < 6; stringIdx++) {
    for (let fret = 0; fret <= 12; fret++) {
      if ((STRING_TUNINGS[stringIdx] + fret) % 12 === noteIndex) {
        positions.push({ stringIdx, fret });
      }
    }
  }
  positions.sort((a, b) => {
    if (a.stringIdx !== b.stringIdx) return b.stringIdx - a.stringIdx;
    return a.fret - b.fret;
  });
  return filterPoolByStringSet(positions, stringSet);
}

/** Curated memorization games (common fretboard-trainer patterns). */
type MemorizationGameId =
  | 'flash-cards'
  | 'ear-then-see'
  | 'fretboard-forever'
  | 'quiz-name-note'
  | 'quiz-name-fret'
  | 'chromatic-crawl';

type ForeverPacingMode = 'manual' | 'auto';

const MEMORIZATION_GAME_LABELS: Record<MemorizationGameId, string> = {
  'flash-cards': 'Flash cards — random spot (see + optional sound)',
  'ear-then-see': 'Hear it → find it — sound first, neck appears after',
  'fretboard-forever': 'Same note, whole neck — every position in order',
  'quiz-name-note': 'Quiz — tap the note name (no guitar)',
  'quiz-name-fret': 'Quiz — tap the fret (string + note shown)',
  'chromatic-crawl': 'Chromatic crawl — half-steps on one string',
};

const MEMORIZATION_GAME_HELP: Record<MemorizationGameId, string> = {
  'flash-cards':
    'Classic call-out: note, string, and fret together with the matching dot. Use “seconds per card” as relaxed pacing — it is not a click track.',
  'ear-then-see':
    'Each round plays the pitch (if enabled), then hides the answer for a few seconds so you picture the neck. The dot appears automatically, or tap Reveal when you are ready.',
  'fretboard-forever':
    'Walks every occurrence of one note across the neck (respects your string filter). Manual stepping is best for learning; auto is a slow tour.',
  'quiz-name-note':
    'The neck shows one “?” dot. Choose the correct pitch name. Wrong taps show the answer on the board so you still learn.',
  'quiz-name-fret':
    'Given the string and the note, tap Open or 1–12. One correct fret per string for each pitch — your practice pool keeps difficulty realistic.',
  'chromatic-crawl':
    'Half-step by half-step from the nut to the 12th fret and back on one string. Great for spacing and ear-hand sync; use a slow seconds-per-step pace.',
};

function pickRandomPoolPosition(
  pool: Array<{ stringIdx: number; fret: number }>
): { note: string; stringIdx: number; fret: number } | null {
  if (pool.length === 0) return null;
  const { stringIdx, fret } = pool[Math.floor(Math.random() * pool.length)]!;
  return { note: getNoteAtPosition(stringIdx, fret), stringIdx, fret };
}

function FretboardNoteDrill() {
  const [game, setGame] = useState<MemorizationGameId>('flash-cards');
  const [paceSeconds, setPaceSeconds] = useState(6);
  const [earRevealSeconds, setEarRevealSeconds] = useState(4);
  const [isPlaying, setIsPlaying] = useState(false);
  const [playReferenceNote, setPlayReferenceNote] = useState(true);
  const [earAnswerVisible, setEarAnswerVisible] = useState(false);
  const [drillMode, setDrillMode] = useState<NoteDrillMode>('region-low');
  const [stringSet, setStringSet] = useState<StringSet>('all');
  const [sequenceDirection, setSequenceDirection] = useState<'low-to-high' | 'high-to-low'>('low-to-high');
  const [foreverPacing, setForeverPacing] = useState<ForeverPacingMode>('manual');
  const [selectedString, setSelectedString] = useState(0);
  const [selectedNote, setSelectedNote] = useState('C');
  const [crawlStringIdx, setCrawlStringIdx] = useState(0);
  const [currentPrompt, setCurrentPrompt] = useState<{ note: string; stringIdx: number; fret: number } | null>(null);
  const [foreverIndex, setForeverIndex] = useState(0);
  const foreverSeqIndexRef = useRef(0);
  const chromaticStepRef = useRef<{ fret: number; dir: 1 | -1 }>({ fret: 0, dir: 1 });
  const earRevealTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [deskPrompt, setDeskPrompt] = useState<{ note: string; stringIdx: number; fret: number } | null>(null);
  const [deskFeedback, setDeskFeedback] = useState<'correct' | 'wrong' | null>(null);
  const [deskLastGuess, setDeskLastGuess] = useState<string | number | null>(null);
  const [deskStreak, setDeskStreak] = useState(0);
  const [deskTotal, setDeskTotal] = useState(0);

  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);

  const rawPool = useMemo(
    () => buildNoteDrillPool(drillMode, selectedString, selectedNote),
    [drillMode, selectedString, selectedNote]
  );
  const pool = useMemo(() => filterPoolByStringSet(rawPool, stringSet), [rawPool, stringSet]);

  const allowedStrings = useMemo(() => getStringSetIndices(stringSet), [stringSet]);
  const crawlStringIdxSafe = allowedStrings.includes(crawlStringIdx) ? crawlStringIdx : allowedStrings[0] ?? 0;

  const foreverSequence = useMemo(() => {
    const seq = buildSameNoteSequence(selectedNote, stringSet);
    return sequenceDirection === 'high-to-low' ? [...seq].reverse() : seq;
  }, [selectedNote, stringSet, sequenceDirection]);

  const clearEarTimer = useCallback(() => {
    if (earRevealTimerRef.current) {
      clearTimeout(earRevealTimerRef.current);
      earRevealTimerRef.current = null;
    }
  }, []);

  const stopTimer = useCallback(() => {
    clearEarTimer();
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    setIsPlaying(false);
    setCurrentPrompt(null);
    setEarAnswerVisible(false);
  }, [clearEarTimer]);

  const resetDesk = useCallback(() => {
    setDeskFeedback(null);
    setDeskLastGuess(null);
  }, []);

  const changeGame = useCallback(
    (next: MemorizationGameId) => {
      stopTimer();
      resetDesk();
      setCurrentPrompt(null);
      setEarAnswerVisible(false);
      clearEarTimer();
      setForeverIndex(0);
      chromaticStepRef.current = { fret: 0, dir: 1 };
      setGame(next);
    },
    [stopTimer, resetDesk, clearEarTimer]
  );

  useEffect(() => {
    const allowed = getStringSetIndices(stringSet);
    if (!allowed.includes(crawlStringIdx) && allowed[0] !== undefined) {
      setCrawlStringIdx(allowed[0]);
    }
  }, [stringSet, crawlStringIdx]);

  useEffect(() => {
    audioContextRef.current = new (window.AudioContext || (window as any).webkitAudioContext)();
    return () => {
      clearEarTimer();
      if (intervalRef.current) clearInterval(intervalRef.current);
      if (audioContextRef.current) audioContextRef.current.close();
    };
  }, [clearEarTimer]);

  const playGuitarNoteAt = (stringIdx: number, fret: number) => {
    if (!audioContextRef.current) return;
    const context = audioContextRef.current;
    void resumeAudioContext(context);
    playGuitarStringFret(context, stringIdx, fret, context.currentTime, {
      peakGain: 0.28,
      durationScale: 0.95,
    });
  };

  const readChromaticStep = useCallback((): { note: string; stringIdx: number; fret: number } => {
    const stringIdx = crawlStringIdxSafe;
    const { fret } = chromaticStepRef.current;
    return { note: getNoteAtPosition(stringIdx, fret), stringIdx, fret };
  }, [crawlStringIdxSafe]);

  const advanceChromaticStep = useCallback(() => {
    const { fret, dir } = chromaticStepRef.current;
    const nf = fret + dir;
    let nextFret = nf;
    let nextDir = dir;
    if (nf > 12) {
      nextFret = 11;
      nextDir = -1;
    } else if (nf < 0) {
      nextFret = 1;
      nextDir = 1;
    }
    chromaticStepRef.current = { fret: nextFret, dir: nextDir };
  }, []);

  const drawDeskNoteQuestion = useCallback(() => {
    const p = pickRandomPoolPosition(pool);
    setDeskPrompt(p);
    setDeskFeedback(null);
    setDeskLastGuess(null);
  }, [pool]);

  const drawDeskFretQuestion = useCallback(() => {
    const p = pickRandomPoolPosition(pool);
    setDeskPrompt(p);
    setDeskFeedback(null);
    setDeskLastGuess(null);
  }, [pool]);

  useEffect(() => {
    if (game !== 'quiz-name-note' && game !== 'quiz-name-fret') return;
    if (game === 'quiz-name-note') drawDeskNoteQuestion();
    else drawDeskFretQuestion();
  }, [game, drillMode, stringSet, selectedString, selectedNote, drawDeskNoteQuestion, drawDeskFretQuestion]);

  useEffect(() => {
    setForeverIndex(0);
  }, [selectedNote, stringSet, sequenceDirection]);

  const foreverPromptAt = useCallback(
    (idx: number): { note: string; stringIdx: number; fret: number } | null => {
      if (foreverSequence.length === 0) return null;
      const i = ((idx % foreverSequence.length) + foreverSequence.length) % foreverSequence.length;
      const { stringIdx, fret } = foreverSequence[i]!;
      return { note: getNoteAtPosition(stringIdx, fret), stringIdx, fret };
    },
    [foreverSequence]
  );

  const startFlashCards = () => {
    if (pool.length === 0) return;
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    setIsPlaying(true);
    const intervalMs = paceSeconds * 1000;
    const tick = () => {
      const next = pickRandomPoolPosition(pool);
      if (!next) return;
      setCurrentPrompt(next);
      if (playReferenceNote) playGuitarNoteAt(next.stringIdx, next.fret);
    };
    tick();
    intervalRef.current = setInterval(tick, intervalMs);
  };

  const startEarThenSee = () => {
    if (pool.length === 0) return;
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    setIsPlaying(true);
    const intervalMs = paceSeconds * 1000;
    const tick = () => {
      clearEarTimer();
      const next = pickRandomPoolPosition(pool);
      if (!next) return;
      setCurrentPrompt(next);
      setEarAnswerVisible(false);
      if (playReferenceNote) playGuitarNoteAt(next.stringIdx, next.fret);
      const cap = Math.max(400, paceSeconds * 1000 - 600);
      const revealMs = Math.min(earRevealSeconds * 1000, cap);
      earRevealTimerRef.current = setTimeout(() => setEarAnswerVisible(true), revealMs);
    };
    tick();
    intervalRef.current = setInterval(tick, intervalMs);
  };

  const startForeverAuto = () => {
    if (foreverSequence.length === 0) return;
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    foreverSeqIndexRef.current = 0;
    setIsPlaying(true);
    const intervalMs = paceSeconds * 1000;
    const tick = () => {
      const idx = foreverSeqIndexRef.current;
      const next = foreverPromptAt(idx);
      if (!next) return;
      setForeverIndex(idx);
      setCurrentPrompt(next);
      foreverSeqIndexRef.current = idx + 1;
      if (playReferenceNote) playGuitarNoteAt(next.stringIdx, next.fret);
    };
    tick();
    intervalRef.current = setInterval(tick, intervalMs);
  };

  const startChromaticCrawl = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    chromaticStepRef.current = { fret: 0, dir: 1 };
    setIsPlaying(true);
    const intervalMs = paceSeconds * 1000;
    const tick = () => {
      const cur = readChromaticStep();
      setCurrentPrompt({ ...cur });
      if (playReferenceNote) playGuitarNoteAt(cur.stringIdx, cur.fret);
      advanceChromaticStep();
    };
    tick();
    intervalRef.current = setInterval(tick, intervalMs);
  };

  const toggleSession = () => {
    if (isPlaying) {
      stopTimer();
      return;
    }
    if (game === 'flash-cards') startFlashCards();
    else if (game === 'ear-then-see') startEarThenSee();
    else if (game === 'fretboard-forever' && foreverPacing === 'auto') startForeverAuto();
    else if (game === 'chromatic-crawl') startChromaticCrawl();
  };

  useEffect(() => {
    if (game !== 'fretboard-forever' || foreverPacing !== 'manual' || foreverSequence.length === 0) return;
    setCurrentPrompt(foreverPromptAt(foreverIndex));
  }, [game, foreverPacing, foreverIndex, foreverPromptAt, foreverSequence.length]);

  useEffect(() => {
    if (game !== 'fretboard-forever' || foreverPacing !== 'manual') return;
    if (foreverSequence.length === 0) {
      setCurrentPrompt(null);
      return;
    }
    setForeverIndex((i) => Math.min(i, foreverSequence.length - 1));
  }, [foreverSequence, game, foreverPacing]);

  useEffect(() => {
    if (game !== 'chromatic-crawl' || isPlaying) return;
    chromaticStepRef.current = { fret: 0, dir: 1 };
    setCurrentPrompt(readChromaticStep());
  }, [game, isPlaying, crawlStringIdxSafe, readChromaticStep]);

  const foreverStep = (delta: number) => {
    if (foreverSequence.length === 0) return;
    setForeverIndex((i) => {
      const len = foreverSequence.length;
      return (i + delta + len * 10) % len;
    });
  };

  const handleDeskNoteGuess = (guess: string) => {
    if (!deskPrompt || deskFeedback === 'correct') return;
    setDeskLastGuess(guess);
    if (guess === deskPrompt.note) {
      setDeskFeedback('correct');
      setDeskStreak((s) => s + 1);
      setDeskTotal((t) => t + 1);
      window.setTimeout(() => drawDeskNoteQuestion(), 650);
    } else {
      setDeskFeedback('wrong');
      setDeskStreak(0);
    }
  };

  const handleDeskFretGuess = (fretGuess: number) => {
    if (!deskPrompt || deskFeedback === 'correct') return;
    setDeskLastGuess(fretGuess);
    if (fretGuess === deskPrompt.fret) {
      setDeskFeedback('correct');
      setDeskStreak((s) => s + 1);
      setDeskTotal((t) => t + 1);
      window.setTimeout(() => drawDeskFretQuestion(), 650);
    } else {
      setDeskFeedback('wrong');
      setDeskStreak(0);
    }
  };

  const usesPracticePool =
    game === 'flash-cards' ||
    game === 'ear-then-see' ||
    game === 'quiz-name-note' ||
    game === 'quiz-name-fret';

  const showForeverControls = game === 'fretboard-forever';
  const showChromaticString = game === 'chromatic-crawl';
  const showTimedControls =
    game === 'flash-cards' ||
    game === 'ear-then-see' ||
    (game === 'fretboard-forever' && foreverPacing === 'auto') ||
    game === 'chromatic-crawl';

  const showStartStop =
    game === 'flash-cards' ||
    game === 'ear-then-see' ||
    (game === 'fretboard-forever' && foreverPacing === 'auto') ||
    game === 'chromatic-crawl';

  const highlightForFretboard = (): HighlightNote | undefined => {
    if (game === 'quiz-name-note' && deskPrompt) {
      if (deskFeedback === 'wrong' || deskFeedback === 'correct') {
        return { stringIdx: deskPrompt.stringIdx, fret: deskPrompt.fret, note: deskPrompt.note };
      }
      return { stringIdx: deskPrompt.stringIdx, fret: deskPrompt.fret, note: deskPrompt.note, label: '?' };
    }
    if (game === 'quiz-name-fret' && deskPrompt) {
      if (deskFeedback === 'wrong') {
        return { stringIdx: deskPrompt.stringIdx, fret: deskPrompt.fret, note: deskPrompt.note };
      }
      return undefined;
    }
    if (game === 'ear-then-see' && currentPrompt) {
      if (!earAnswerVisible) return undefined;
      return { stringIdx: currentPrompt.stringIdx, fret: currentPrompt.fret, note: currentPrompt.note };
    }
    if (currentPrompt) {
      return { stringIdx: currentPrompt.stringIdx, fret: currentPrompt.fret, note: currentPrompt.note };
    }
    return undefined;
  };

  const paceDisabled = isPlaying;
  const revealEarNow = () => {
    clearEarTimer();
    setEarAnswerVisible(true);
  };

  return (
    <div className="note-drill-container">
      <div className="note-drill-card">
        <h2>Memorization games</h2>
        <p className="note-drill-description">
          Pick a classic drill from the list. Timed games use <strong>seconds between rounds</strong> (not a metronome).
          Narrow the practice pool until it feels easy, then widen it.
        </p>

        <div className="note-drill-mode note-drill-mode--game">
          <label htmlFor="mem-game-select">Game</label>
          <select
            id="mem-game-select"
            value={game}
            onChange={(e) => changeGame(e.target.value as MemorizationGameId)}
            disabled={isPlaying}
          >
            {(Object.keys(MEMORIZATION_GAME_LABELS) as MemorizationGameId[]).map((id) => (
              <option key={id} value={id}>
                {MEMORIZATION_GAME_LABELS[id]}
              </option>
            ))}
          </select>
        </div>
        <p className="note-drill-mode-help">{MEMORIZATION_GAME_HELP[game]}</p>

        <div className="note-drill-controls">
          {showTimedControls && (
            <>
              <div className="note-drill-bpm note-drill-pace">
                <label>{game === 'chromatic-crawl' ? 'Seconds per step' : 'Seconds per round'}</label>
                <input
                  type="range"
                  min={2}
                  max={30}
                  step={1}
                  value={paceSeconds}
                  onChange={(e) => setPaceSeconds(Number(e.target.value))}
                  disabled={paceDisabled}
                  className="bpm-slider note-drill-pace-slider"
                />
                <input
                  type="number"
                  min={2}
                  max={30}
                  value={paceSeconds}
                  onChange={(e) => setPaceSeconds(Math.min(30, Math.max(2, Number(e.target.value) || 2)))}
                  disabled={paceDisabled}
                  className="bpm-input"
                />
              </div>
              {game === 'ear-then-see' && (
                <div className="note-drill-bpm note-drill-pace">
                  <label>Reveal answer after (seconds)</label>
                  <input
                    type="range"
                    min={1}
                    max={12}
                    step={1}
                    value={earRevealSeconds}
                    onChange={(e) => setEarRevealSeconds(Number(e.target.value))}
                    disabled={paceDisabled}
                    className="bpm-slider note-drill-pace-slider"
                  />
                  <input
                    type="number"
                    min={1}
                    max={12}
                    value={earRevealSeconds}
                    onChange={(e) => setEarRevealSeconds(Math.min(12, Math.max(1, Number(e.target.value) || 1)))}
                    disabled={paceDisabled}
                    className="bpm-input"
                  />
                </div>
              )}
              <label className="note-drill-play-along">
                <input
                  type="checkbox"
                  checked={playReferenceNote}
                  onChange={(e) => setPlayReferenceNote(e.target.checked)}
                  disabled={paceDisabled}
                />
                Play sample pitch each round
              </label>
            </>
          )}

          {showForeverControls && (
            <>
              <div className="note-drill-option">
                <label>Note to trace</label>
                <select
                  value={selectedNote}
                  onChange={(e) => setSelectedNote(e.target.value)}
                  disabled={isPlaying}
                >
                  {NOTES.map((n) => (
                    <option key={n} value={n}>
                      {n}
                    </option>
                  ))}
                </select>
              </div>
              <div className="note-drill-option">
                <label>Direction</label>
                <select
                  value={sequenceDirection}
                  onChange={(e) =>
                    setSequenceDirection(e.target.value as 'low-to-high' | 'high-to-low')
                  }
                  disabled={isPlaying}
                >
                  <option value="low-to-high">Low pitch → high pitch</option>
                  <option value="high-to-low">High pitch → low pitch</option>
                </select>
              </div>
              <div className="note-drill-option">
                <label>Pacing</label>
                <select
                  value={foreverPacing}
                  onChange={(e) => {
                    const v = e.target.value as ForeverPacingMode;
                    stopTimer();
                    setForeverPacing(v);
                    setForeverIndex(0);
                  }}
                  disabled={isPlaying}
                >
                  <option value="manual">Manual (Next / Prev)</option>
                  <option value="auto">Auto (uses seconds per round)</option>
                </select>
              </div>
            </>
          )}

          {showChromaticString && (
            <div className="note-drill-option">
              <label>String for crawl</label>
              <select
                value={crawlStringIdxSafe}
                onChange={(e) => setCrawlStringIdx(Number(e.target.value))}
                disabled={isPlaying}
              >
                {allowedStrings.map((idx) => (
                  <option key={idx} value={idx}>
                    {STRING_NAMES[idx]}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div className="note-drill-mode">
            <label>Strings</label>
            <select
              value={stringSet}
              onChange={(e) => setStringSet(e.target.value as StringSet)}
              disabled={isPlaying}
            >
              <option value="all">All strings</option>
              <option value="high-e">High E only</option>
              <option value="top-2">Top 2 (High E + B)</option>
              <option value="top-3">Top 3 (E + B + G)</option>
              <option value="bottom-2">Bottom 2 (A + Low E)</option>
              <option value="bottom-3">Bottom 3 (D + A + Low E)</option>
              <option value="low-e">Low E only</option>
            </select>
          </div>

          {usesPracticePool && (
            <>
              <div className="note-drill-mode">
                <label>Practice pool</label>
                <select
                  value={drillMode}
                  onChange={(e) => setDrillMode(e.target.value as NoteDrillMode)}
                  disabled={isPlaying}
                >
                  <option value="region-low">Open – 4th fret (all strings)</option>
                  <option value="region-mid">5th – 9th fret</option>
                  <option value="region-high">10th – 12th fret</option>
                  <option value="single-string">Single string</option>
                  <option value="single-note">One note, all frets (12)</option>
                  <option value="naturals-only">Natural notes only</option>
                  <option value="all-random">Full neck (0–12)</option>
                </select>
              </div>
              {drillMode === 'single-string' && (
                <div className="note-drill-option">
                  <label>String</label>
                  <select
                    value={selectedString}
                    onChange={(e) => setSelectedString(Number(e.target.value))}
                    disabled={isPlaying}
                  >
                    {STRING_NAMES.map((name, i) => (
                      <option key={i} value={i}>
                        {name}
                      </option>
                    ))}
                  </select>
                </div>
              )}
              {drillMode === 'single-note' && (
                <div className="note-drill-option">
                  <label>Locked note</label>
                  <select
                    value={selectedNote}
                    onChange={(e) => setSelectedNote(e.target.value)}
                    disabled={isPlaying}
                  >
                    {NOTES.map((n) => (
                      <option key={n} value={n}>
                        {n}
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </>
          )}

          {game === 'fretboard-forever' && foreverPacing === 'manual' && foreverSequence.length > 0 && (
            <div className="note-drill-forever-nav">
              <button type="button" className="note-drill-nav-btn" onClick={() => foreverStep(-1)}>
                ← Prev shape
              </button>
              <span className="note-drill-forever-progress">
                Shape {foreverIndex + 1} of {foreverSequence.length}
              </span>
              <button type="button" className="note-drill-nav-btn" onClick={() => foreverStep(1)}>
                Next shape →
              </button>
              <button
                type="button"
                className="note-drill-reveal-btn note-drill-reveal-btn--ghost"
                onClick={() => currentPrompt && playGuitarNoteAt(currentPrompt.stringIdx, currentPrompt.fret)}
              >
                Hear this pitch
              </button>
            </div>
          )}

          {game === 'fretboard-forever' && foreverSequence.length === 0 && (
            <p className="note-drill-empty-pool">No positions for this note with the current string filter.</p>
          )}

          {showStartStop && (
            <button
              className={`play-button ${isPlaying ? 'playing' : ''}`}
              onClick={toggleSession}
              disabled={
                game === 'flash-cards' || game === 'ear-then-see'
                  ? pool.length === 0
                  : game === 'fretboard-forever'
                    ? foreverSequence.length === 0
                    : false
              }
            >
              {isPlaying ? 'Stop' : 'Start'}
            </button>
          )}

          {game === 'ear-then-see' && isPlaying && currentPrompt && !earAnswerVisible && (
            <button type="button" className="note-drill-reveal-btn" onClick={revealEarNow}>
              Reveal answer now
            </button>
          )}

          {game === 'quiz-name-note' && (
            <div className="note-desk-actions">
              <button type="button" className="note-drill-reveal-btn note-drill-reveal-btn--ghost" onClick={drawDeskNoteQuestion}>
                New question
              </button>
              {deskPrompt && (
                <button
                  type="button"
                  className="note-drill-reveal-btn note-drill-reveal-btn--ghost"
                  onClick={() => playGuitarNoteAt(deskPrompt.stringIdx, deskPrompt.fret)}
                >
                  Hear dot
                </button>
              )}
            </div>
          )}

          {game === 'quiz-name-fret' && (
            <div className="note-desk-actions">
              <button type="button" className="note-drill-reveal-btn note-drill-reveal-btn--ghost" onClick={drawDeskFretQuestion}>
                New question
              </button>
            </div>
          )}
        </div>

        {(game === 'flash-cards' ||
          game === 'ear-then-see' ||
          game === 'fretboard-forever' ||
          game === 'chromatic-crawl') && (
          <div className="note-drill-prompt-area">
            {currentPrompt ? (
              <div className="note-drill-prompt-grid">
                {game === 'fretboard-forever' && (
                  <div className="note-drill-block note-drill-block-challenge">
                    <span className="note-drill-block-label">Forever</span>
                    <span className="note-drill-block-value">All {selectedNote} on the neck</span>
                  </div>
                )}
                {game === 'chromatic-crawl' && (
                  <div className="note-drill-block note-drill-block-challenge">
                    <span className="note-drill-block-label">Crawl</span>
                    <span className="note-drill-block-value">Half-steps</span>
                  </div>
                )}
                {game === 'ear-then-see' && (
                  <div className="note-drill-block note-drill-block-challenge">
                    <span className="note-drill-block-label">Ear</span>
                    <span className="note-drill-block-value">
                      {earAnswerVisible ? 'Answer' : 'Listen first'}
                    </span>
                  </div>
                )}
                <div className="note-drill-block note-drill-block-note">
                  <span className="note-drill-block-label">Note</span>
                  <span className="note-drill-block-value note-drill-note-big">
                    {game === 'ear-then-see' && !earAnswerVisible ? '—' : currentPrompt.note}
                  </span>
                </div>
                <div className="note-drill-block note-drill-block-string">
                  <span className="note-drill-block-label">String</span>
                  <span className="note-drill-block-value">
                    {game === 'ear-then-see' && !earAnswerVisible ? '—' : STRING_NAMES[currentPrompt.stringIdx]}
                  </span>
                </div>
                <div className="note-drill-block note-drill-block-fret">
                  <span className="note-drill-block-label">Fret</span>
                  <span className="note-drill-block-value">
                    {game === 'ear-then-see' && !earAnswerVisible
                      ? '—'
                      : currentPrompt.fret === 0
                        ? 'Open'
                        : currentPrompt.fret}
                  </span>
                </div>
              </div>
            ) : (
              <div className="note-drill-prompt-placeholder">
                {game === 'fretboard-forever' && foreverSequence.length === 0
                  ? 'Pick another string filter or note.'
                  : game === 'fretboard-forever'
                    ? foreverPacing === 'manual'
                      ? 'Use Next / Prev to walk the shapes.'
                      : 'Press Start for a slow tour.'
                    : game === 'chromatic-crawl'
                      ? 'Choose a string, then Start (or preview above when idle).'
                      : pool.length === 0
                        ? 'Your practice pool is empty — widen filters.'
                        : 'Press Start to begin.'}
              </div>
            )}
          </div>
        )}

        {game === 'quiz-name-note' && (
          <div className="note-drill-prompt-area">
            {deskPrompt ? (
              <>
                <p className="note-desk-prompt-title">Which note is the highlighted dot?</p>
                <p className="note-desk-sub">Read the neck — then tap the pitch name.</p>
                <div className="note-desk-note-grid">
                  {NOTES.map((n) => (
                    <button
                      key={n}
                      type="button"
                      className={`note-desk-choice ${deskLastGuess === n && deskFeedback === 'wrong' ? 'note-desk-choice--wrong' : ''} ${deskLastGuess === n && deskFeedback === 'correct' ? 'note-desk-choice--correct' : ''}`}
                      onClick={() => handleDeskNoteGuess(n)}
                      disabled={deskFeedback === 'correct'}
                    >
                      {n}
                    </button>
                  ))}
                </div>
                <div className="note-desk-footer">
                  {deskFeedback === 'wrong' && (
                    <span className="note-desk-hint-wrong">Not quite — the board shows the answer.</span>
                  )}
                  {deskFeedback === 'correct' && <span className="note-desk-hint-ok">Nice — next question loading…</span>}
                  <span className="note-desk-streak">
                    Streak <strong>{deskStreak}</strong> · Solved <strong>{deskTotal}</strong>
                  </span>
                </div>
              </>
            ) : (
              <div className="note-drill-prompt-placeholder">Widen your practice pool to get questions.</div>
            )}
          </div>
        )}

        {game === 'quiz-name-fret' && (
          <div className="note-drill-prompt-area">
            {deskPrompt ? (
              <>
                <p className="note-desk-prompt-title">
                  On <strong>{STRING_NAMES[deskPrompt.stringIdx]}</strong>, where is{' '}
                  <strong className="note-desk-accent">{deskPrompt.note}</strong>?
                </p>
                <p className="note-desk-sub">Tap Open or a fret number (0–12).</p>
                <div className="note-desk-fret-row">
                  <button
                    type="button"
                    className={`note-desk-fret-btn ${deskLastGuess === 0 && deskFeedback === 'wrong' ? 'note-desk-choice--wrong' : ''} ${deskLastGuess === 0 && deskFeedback === 'correct' ? 'note-desk-choice--correct' : ''}`}
                    onClick={() => handleDeskFretGuess(0)}
                    disabled={deskFeedback === 'correct'}
                  >
                    Open
                  </button>
                  {Array.from({ length: 12 }, (_, f) => f + 1).map((fret) => (
                    <button
                      key={fret}
                      type="button"
                      className={`note-desk-fret-btn ${deskLastGuess === fret && deskFeedback === 'wrong' ? 'note-desk-choice--wrong' : ''} ${deskLastGuess === fret && deskFeedback === 'correct' ? 'note-desk-choice--correct' : ''}`}
                      onClick={() => handleDeskFretGuess(fret)}
                      disabled={deskFeedback === 'correct'}
                    >
                      {fret}
                    </button>
                  ))}
                </div>
                <div className="note-desk-footer">
                  {deskFeedback === 'wrong' && (
                    <span className="note-desk-hint-wrong">Correct fret is highlighted on the neck.</span>
                  )}
                  {deskFeedback === 'correct' && <span className="note-desk-hint-ok">Yes — next question loading…</span>}
                  <span className="note-desk-streak">
                    Streak <strong>{deskStreak}</strong> · Solved <strong>{deskTotal}</strong>
                  </span>
                </div>
              </>
            ) : (
              <div className="note-drill-prompt-placeholder">Widen your practice pool to get questions.</div>
            )}
          </div>
        )}

        <div className="note-drill-fretboard-wrap">
          <Fretboard
            scaleNotes={NOTES}
            rootNote=""
            mode="scale"
            chordDisplay="notes"
            highlightNote={highlightForFretboard()}
          />
        </div>
      </div>
    </div>
  );
}

function StrumMachine() {
  const [bpm, setBpm] = useState(120);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentStrum, setCurrentStrum] = useState(0);
  const [currentChordIdx, setCurrentChordIdx] = useState(0);
  const [pattern, setPattern] = useState([...COMMON_STRUM_PATTERNS[2].pattern]); // Old Faithful as default
  const [selectedPatternIdx, setSelectedPatternIdx] = useState(2);
  const [selectedKey, setSelectedKey] = useState('C');
  const [selectedProgressionIdx, setSelectedProgressionIdx] = useState(0);
  const [progression, setProgression] = useState<Array<{ chord: string; measures: number }>>([
    { chord: 'C', measures: 1 },
    { chord: 'G', measures: 1 },
    { chord: 'Am', measures: 1 },
    { chord: 'F', measures: 1 },
  ]);
  const [chordCategory, setChordCategory] = useState<ChordCategory>('Standard');
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [useChordProgression, setUseChordProgression] = useState(false);
  const [metronomeStyle, setMetronomeStyle] = useState<StrumMetronomeStyle>('classic');
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const measureCountRef = useRef(0);
  const strumInMeasureRef = useRef(0);

  const strumLabels = [1, '+', 2, '+', 3, '+', 4, '+'];

  useEffect(() => {
    audioContextRef.current = new (window.AudioContext || (window as any).webkitAudioContext)();
    return () => { 
      if (audioContextRef.current) {
        audioContextRef.current.close(); 
      }
    };
  }, []);

  useEffect(() => {
    if (isPlaying) {
      let strumIdx = 0;
      let measureCount = 0;
      let strumsInCurrentMeasure = 0;
      let chordIdx = 0;
      let measuresSinceLastChordChange = 0;
      
      setCurrentStrum(0);
      setCurrentChordIdx(0);
      measureCountRef.current = 0;
      strumInMeasureRef.current = 0;

      const intervalMs = (60000 / bpm) / 2; // Each strum position is an 8th note
      
      intervalRef.current = setInterval(() => {
        setCurrentStrum(strumIdx);

        const ctx = audioContextRef.current;
        if (ctx) {
          playStrumMetronomeTick(ctx, ctx.currentTime, strumIdx, metronomeStyle);
        }

        // Play strum sound for this position if not a rest
        if (pattern[strumIdx] !== '-') {
          playStrumTick(audioContextRef);
        }

        // Move to next strum
        strumIdx = (strumIdx + 1) % 8;
        strumsInCurrentMeasure++;
        
        // Check if we completed a measure (8 strums = 4 beats)
        if (strumsInCurrentMeasure >= 8) {
          strumsInCurrentMeasure = 0;
          measureCount++;
          measuresSinceLastChordChange++;
          
          // Check if we need to change chords
          if (measuresSinceLastChordChange >= progression[chordIdx].measures) {
            chordIdx = (chordIdx + 1) % progression.length;
            setCurrentChordIdx(chordIdx);
            measuresSinceLastChordChange = 0;
          }
        }
        
        measureCountRef.current = measureCount;
        strumInMeasureRef.current = strumsInCurrentMeasure;
      }, intervalMs);
      
      return () => {
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
        }
      };
    } else {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
      setCurrentStrum(0);
    }
  }, [isPlaying, bpm, pattern, progression, metronomeStyle]);

  const toggleStrum = (idx: number) => {
    const newPattern = [...pattern];
    if (newPattern[idx] === '-') {
      newPattern[idx] = 'D';
    } else if (newPattern[idx] === 'D') {
      newPattern[idx] = 'U';
    } else {
      newPattern[idx] = '-';
    }
    setPattern(newPattern);
  };

  const handlePatternChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const idx = Number(e.target.value);
    setSelectedPatternIdx(idx);
    setPattern([...COMMON_STRUM_PATTERNS[idx].pattern]);
  };

  const buildProgressionFromPattern = (pattern: string[], key: string) =>
    pattern.map(degree => ({
      chord: getChordInKey(key, degree),
      measures: 1
    }));

  const handleProgressionChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const idx = Number(e.target.value);
    setSelectedProgressionIdx(idx);
  };

  const handleKeyChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newKey = e.target.value;
    setSelectedKey(newKey);
    
    // If not custom progression, regenerate with new key
  };

  useEffect(() => {
    if (selectedProgressionIdx === 0) return;
    const progressionPattern = CHORD_PROGRESSIONS[selectedProgressionIdx].pattern;
    const newProgression = buildProgressionFromPattern(progressionPattern, selectedKey);
    setProgression(newProgression);
    setCurrentChordIdx(0);
  }, [selectedProgressionIdx, selectedKey]);

  const addChord = () => {
    setProgression([...progression, { chord: 'C', measures: 1 }]);
    setSelectedProgressionIdx(0); // Set to custom when manually editing
  };

  const removeChord = (index: number) => {
    if (progression.length > 1) {
      setProgression(progression.filter((_, i) => i !== index));
      setSelectedProgressionIdx(0); // Set to custom when manually editing
      if (currentChordIdx >= progression.length - 1) {
        setCurrentChordIdx(Math.max(0, progression.length - 2));
      }
    }
  };

  const updateChord = (index: number, chord: string) => {
    const newProgression = [...progression];
    newProgression[index].chord = chord;
    setProgression(newProgression);
    setSelectedProgressionIdx(0); // Set to custom when manually editing
  };

  const updateMeasures = (index: number, measures: number) => {
    const newProgression = [...progression];
    newProgression[index].measures = Math.max(1, Math.min(8, measures));
    setProgression(newProgression);
    setSelectedProgressionIdx(0); // Set to custom when manually editing
  };

  const currentChord = findChordShape(progression[currentChordIdx]?.chord || 'C', chordCategory) || 
                       Object.values(CHORD_CATEGORIES[chordCategory])[0];

  return (
    <div className="strum-machine-layout">
      <div className="strum-machine-container">
        <h2>Strum Machine</h2>
        
        {/* Pattern Selection */}
        <div className="strum-section">
          <h3>Pattern</h3>
          <div className="pattern-selector">
            <select 
              value={selectedPatternIdx} 
              onChange={handlePatternChange}
              disabled={isPlaying}
            >
              {COMMON_STRUM_PATTERNS.map((p, i) => (
                <option key={i} value={i}>{p.name}</option>
              ))}
            </select>
          </div>
          
          {/* Strum Pattern Editor */}
          <div className="strum-pattern-editor">
            {pattern.map((s, idx) => (
              <div
                key={idx}
                className={`strum-box ${currentStrum === idx && isPlaying ? 'active' : ''}`}
                onClick={() => !isPlaying && toggleStrum(idx)}
                title={`Click to cycle: - → D → U → -`}
              >
                <div className="strum-arrow">{s === 'D' ? '↓' : s === 'U' ? '↑' : '–'}</div>
                <div className="strum-label">{strumLabels[idx]}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Chord progression: button + checkbox */}
        <div className="strum-section strum-chord-options">
          <button
            type="button"
            className="specify-progression-btn"
            onClick={() => setDrawerOpen(true)}
          >
            Specify chord progression
          </button>
          <label className="use-chord-progression-label">
            <input
              type="checkbox"
              checked={useChordProgression}
              onChange={(e) => setUseChordProgression(e.target.checked)}
            />
            <span>Use chord progression</span>
          </label>
        </div>

        {/* BPM and Controls */}
        <div className="strum-section">
          <div className="bpm-control">
            <label>BPM: {bpm}</label>
            <input
              type="range"
              min="40"
              max="240"
              value={bpm}
              onChange={(e) => setBpm(Number(e.target.value))}
              className="bpm-slider"
            />
            <input
              type="number"
              min="40"
              max="240"
              value={bpm}
              onChange={(e) => setBpm(Number(e.target.value))}
              className="bpm-input"
            />
          </div>

          <div className="strum-metronome-select">
            <label>
              Metronome:
              <select
                value={metronomeStyle}
                onChange={(e) => setMetronomeStyle(e.target.value as StrumMetronomeStyle)}
                disabled={isPlaying}
                aria-label="Metronome sound and beat pattern"
              >
                {STRUM_METRONOME_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </label>
          </div>

          <button 
            className={`play-button ${isPlaying ? 'playing' : ''}`}
            onClick={() => setIsPlaying(!isPlaying)}
          >
            {isPlaying ? '⏸ Stop' : '▶ Play'}
          </button>
        </div>

        {/* Current chord + fretboard: only when playing and use chord progression is enabled */}
        {isPlaying && useChordProgression && (
          <div className="strum-fretboard">
            <h3>Current chord: {progression[currentChordIdx]?.chord}</h3>
            <Fretboard
              scaleNotes={[]}
              rootNote={progression[currentChordIdx]?.chord || 'C'}
              mode={'chord'}
              chord={currentChord}
              chordDisplay={'notes'}
            />
          </div>
        )}
      </div>

      {/* Right-side drawer: chord progression editor */}
      <div className={`strum-drawer-overlay ${drawerOpen ? 'open' : ''}`} onClick={() => setDrawerOpen(false)} aria-hidden={!drawerOpen} />
      <div className={`strum-drawer ${drawerOpen ? 'open' : ''}`}>
        <div className="strum-drawer-header">
          <h3>Chord progression</h3>
          <button type="button" className="strum-drawer-close" onClick={() => setDrawerOpen(false)} aria-label="Close">×</button>
        </div>
        <div className="strum-drawer-body">
          <div className="key-progression-selectors progression-toolbar">
            <div className="key-selector">
              <label>
                Key:
                <select value={selectedKey} onChange={handleKeyChange}>
                  {NOTES.map(note => (
                    <option key={note} value={note}>{note}</option>
                  ))}
                </select>
              </label>
            </div>
            <div className="progression-pattern-selector">
              <label>
                Pattern:
                <select value={selectedProgressionIdx} onChange={handleProgressionChange}>
                  {CHORD_PROGRESSIONS.map((prog, i) => (
                    <option key={i} value={i}>{prog.name}</option>
                  ))}
                </select>
              </label>
            </div>
          </div>
          <div className="chord-category-selector">
            <label>
              Category:
              <select value={chordCategory} onChange={(e) => setChordCategory(e.target.value as ChordCategory)}>
                {Object.keys(CHORD_CATEGORIES).map(category => (
                  <option key={category} value={category}>{category}</option>
                ))}
              </select>
            </label>
          </div>
          <div className="section-header">
            <span>Chords</span>
            <button type="button" onClick={addChord} className="add-chord-btn compact">+ Add chord</button>
          </div>
          <div className="chord-progression compact">
            {progression.map((item, i) => (
              <div key={i} className="chord-item compact">
                <select value={item.chord} onChange={(e) => updateChord(i, e.target.value)}>
                  {Object.keys(CHORD_CATEGORIES).map(cat => (
                    <optgroup key={cat} label={cat}>
                      {Object.keys(CHORD_CATEGORIES[cat as ChordCategory]).map(chord => (
                        <option key={`${cat}-${chord}`} value={chord}>{chord}</option>
                      ))}
                    </optgroup>
                  ))}
                </select>
                <div className="measures-control">
                  <label>Bars:</label>
                  <input
                    type="number"
                    min={1}
                    max={8}
                    value={item.measures}
                    onChange={(e) => updateMeasures(i, Number(e.target.value))}
                  />
                </div>
                <button
                  type="button"
                  onClick={() => removeChord(i)}
                  disabled={progression.length <= 1}
                  className="remove-btn"
                >
                  ✕
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function App() {
  const [mode, setMode] = useState<Mode>('scale');
  const [scaleCategory, setScaleCategory] = useState<ScaleCategory>('Common');
  const [scaleType, setScaleType] = useState<ScaleType>('Minor Pentatonic');
  const [key, setKey] = useState('C');
  const [selectedChord, setSelectedChord] = useState('C');
  const [chordCategory, setChordCategory] = useState<ChordCategory>('Standard');
  const [chordDisplay, setChordDisplay] = useState<ChordDisplay>('notes');
  const [chordPositionMode, setChordPositionMode] = useState<ChordPositionMode>('single');
  const [theme, setTheme] = useState<'light' | 'dark'>('dark');
  
  const scaleNotes = getScaleNotes(key, SCALES[scaleType]);

  // When scale category changes, pick a scale that exists in the new group
  useEffect(() => {
    const scalesInGroup = SCALE_GROUPS[scaleCategory];
    if (!scalesInGroup.includes(scaleType)) {
      setScaleType(scalesInGroup[0]);
    }
  }, [scaleCategory, scaleType]);

  // When category changes, select the first chord in that category
  useEffect(() => {
    const chordsInCategory = Object.keys(CHORD_CATEGORIES[chordCategory]);
    if (chordsInCategory.length > 0 && !chordsInCategory.includes(selectedChord)) {
      setSelectedChord(chordsInCategory[0]);
    }
  }, [chordCategory, selectedChord]);

  return (
    <div className={`App ${theme}`}>
      <header className="app-header">
        <h1 className="app-title">Fretboard.info</h1>
        <p className="app-tagline">Your daily guitar practice app</p>
      </header>
      
      <div className="mode-toggle" role="tablist" aria-label="Practice mode">
        <button 
          className={mode === 'scale' ? 'active' : ''} 
          onClick={() => setMode('scale')}
        >
          Scale Mode
        </button>
        <button 
          className={mode === 'chord' ? 'active' : ''} 
          onClick={() => setMode('chord')}
        >
          Chord Mode
        </button>
        <button
          className={mode === 'strum' ? 'active' : ''}
          onClick={() => setMode('strum')}
        >
          Strum Machine
        </button>
        <button
          className={mode === 'ear' ? 'active' : ''}
          onClick={() => setMode('ear')}
        >
          Interval Practice
        </button>
        <button
          className={mode === 'notes' ? 'active' : ''}
          onClick={() => setMode('notes')}
        >
          Memorization games
        </button>
      </div>

      {mode === 'scale' ? (
        <div className="controls scale-mode-controls">
          <label>
            Scale group:
            <select
              value={scaleCategory}
              onChange={e => setScaleCategory(e.target.value as ScaleCategory)}
              aria-label="Scale group"
            >
              <option value="Common">Common scales</option>
              <option value="Advanced">Advanced scales</option>
            </select>
          </label>
          <label>
            Scale:
            <select value={scaleType} onChange={e => setScaleType(e.target.value as ScaleType)}>
              {SCALE_GROUPS[scaleCategory].map(scale => (
                <option key={scale} value={scale}>{scale}</option>
              ))}
            </select>
          </label>
          <label>
            Key:
            <select value={key} onChange={e => setKey(e.target.value)}>
              {NOTES.map(note => (
                <option key={note} value={note}>{note}</option>
              ))}
            </select>
          </label>
        </div>
      ) : mode === 'chord' ? (
        <div className="controls">
          <label>
            Category:
            <select value={chordCategory} onChange={e => setChordCategory(e.target.value as ChordCategory)}>
              {Object.keys(CHORD_CATEGORIES).map(category => (
                <option key={category} value={category}>{category}</option>
              ))}
            </select>
          </label>
          <label>
            Chord:
            <select value={selectedChord} onChange={e => setSelectedChord(e.target.value)}>
              {Object.keys(CHORD_CATEGORIES[chordCategory]).map(chord => (
                <option key={chord} value={chord}>{chord}</option>
              ))}
            </select>
          </label>
          <div className="chord-display-options">
            <label>
              <input 
                type="radio" 
                value="notes" 
                checked={chordDisplay === 'notes'}
                onChange={() => setChordDisplay('notes')}
              />
              Show Notes
            </label>
            <label>
              <input 
                type="radio" 
                value="fingers" 
                checked={chordDisplay === 'fingers'}
                onChange={() => setChordDisplay('fingers')}
              />
              Show Finger Numbers
            </label>
          </div>
          <div className="chord-position-options">
            <label>
              <input 
                type="radio" 
                value="single" 
                checked={chordPositionMode === 'single'}
                onChange={() => setChordPositionMode('single')}
              />
              Single Position
            </label>
            <label>
              <input 
                type="radio" 
                value="all-positions" 
                checked={chordPositionMode === 'all-positions'}
                onChange={() => setChordPositionMode('all-positions')}
              />
              All Positions
            </label>
          </div>
          {chordPositionMode === 'all-positions' && (
            <div className="chord-tone-legend" aria-label="Chord tone colors">
              <span className="chord-tone-legend-item root"><span className="dot" /> Root</span>
              <span className="chord-tone-legend-item third"><span className="dot" /> 3rd</span>
              <span className="chord-tone-legend-item fifth"><span className="dot" /> 5th</span>
              <span className="chord-tone-legend-item seventh"><span className="dot" /> 7th</span>
            </div>
          )}
        </div>
      ) : mode === 'strum' ? (
        <StrumMachine />
      ) : mode === 'ear' ? (
        <EarTraining />
      ) : mode === 'notes' ? (
        <FretboardNoteDrill />
      ) : null}
      
      {mode !== 'strum' && mode !== 'ear' && mode !== 'notes' && (
        <Fretboard 
          scaleNotes={scaleNotes} 
          rootNote={key} 
          mode={mode}
          chord={mode === 'chord' ? CHORD_CATEGORIES[chordCategory][selectedChord] : undefined}
          chordDisplay={chordDisplay}
          allChordPositions={mode === 'chord' && chordPositionMode === 'all-positions' ? findAllChordPositions(selectedChord) : undefined}
          chordPositionMode={mode === 'chord' ? chordPositionMode : undefined}
          chordName={mode === 'chord' ? selectedChord : undefined}
        />
      )}
      
      {mode !== 'strum' && mode !== 'ear' && mode !== 'notes' && <Metronome />}
      
      <button 
        className="theme-toggle"
        onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')}
        aria-label="Toggle theme"
      >
        {theme === 'dark' ? '☀️' : '🌙'}
      </button>

      <footer className="app-footer">
        Application built by Jordan Yankovich
      </footer>
    </div>
  );
}

export default App;
